import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Package, Search, Plus, Minus, ArrowRightLeft, RefreshCw, AlertTriangle, CheckCircle, XCircle, MapPin, Trash2, Eye, EyeOff, History, Settings, Inbox, Check, X, Undo2, LayoutGrid, List, Calendar, Download, Lock } from "lucide-react";
import ErrorBoundary from "@/components/ErrorBoundary";
import { TransferNotifications } from "@/components/TransferNotifications";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tracking-only locations: these are AUDIT records (items already given to customers),
// NOT real available stock. Excluded from total_qty calculations.
const TRACKING_ONLY_LOCATIONS = ["Warranty - Claim", "Warranty - Defective", "Warranty - Processing"];
const isTrackingLocation = (loc) => {
  if (!loc) return false;
  const norm = String(loc).trim().toLowerCase();
  return norm === "warranty - claim" || norm === "warranty - defective" || norm === "warranty - processing";
};

const BRANCHES = {
  "QC": "Quezon City",
  "PQ": "Parañaque",
  "CV": "Cavite",
  "PM": "Pampanga",
  "VZ": "Valenzuela",
  "OTG-A": "Orion On The Go - A",
  "OTG-B": "Orion On The Go - B",
  "OTG-C": "Orion On The Go - C"
};

// Branch-specific location options
const BRANCH_LOCATIONS = {
  "QC": [
    "Display Area",
    "IF Restroom",
    "IF Stairs",
    "Room 1",
    "Room 2",
    "Under Racks",
    "Stock Room"
  ],
  "PQ": [
    "Display Area",
    "Stock Room",
    "Inside Room",
    "Under Rack",
    "Long Table",
    "Inventory Room",
    "Back Area"
  ],
  "CV": [
    "Display Area",
    "S1 East",
    "S1 West",
    "S1 Center",
    "S1 North",
    "S2 North",
    "S2 East",
    "S2 West",
    "S2 Center",
    "PATBOX-001",
    "PATBOX-002",
    "Inventory Room",
    "Hallway 1",
    "Hallway 2"
  ],
  "PM": [
    "Display Area",
    "Stock Room",
    "Under Racks"
  ],
  "VZ": [
    "Display Area",
    "Stock Room"
  ]
};

// Get locations for current branch (with custom location support)
const getLocationsForBranch = (branch) => {
  return BRANCH_LOCATIONS[branch] || ["Display Area", "Stock Room"];
};

// Action types
const ACTIONS = {
  RECEIVE: { label: "Receive Stock", icon: Plus, color: "bg-green-600 hover:bg-green-700", description: "Add new stock to inventory" },
  PULL_OUT: { label: "Pull Out", icon: Minus, color: "bg-red-600 hover:bg-red-700", description: "Move stock (internal movement)" },
  TRANSFER: { label: "Transfer", icon: ArrowRightLeft, color: "bg-orange-600 hover:bg-orange-700", description: "Transfer to another branch" },
  RECOUNT: { label: "Recount", icon: RefreshCw, color: "bg-blue-600 hover:bg-blue-700", description: "Adjust stock count & location" }
};

export default function BranchAllStocks() {
  const { user, isAdmin } = useAuth();
  
  // Branch restriction logic - check both is_admin and can_access_all_branches
  const canAccessAllBranches = user?.can_access_all_branches || user?.is_admin || user?.role === "admin" || false;
  const userBranches = user?.assigned_branches?.length ? user.assigned_branches : (user?.assigned_branch ? [user.assigned_branch] : []);
  const userBranch = userBranches[0] || null;
  
  // Users without a branch AND not admin cannot edit
  // They can only VIEW the inventory
  const canEdit = canAccessAllBranches || !!userBranch;
  
  const [selectedBranch, setSelectedBranch] = useState(() => {
    // If user is locked to specific branches, default to first assigned
    if (!canAccessAllBranches && userBranch) {
      return userBranch;
    }
    return "QC";
  });
  
  const [inventory, setInventory] = useState([]);
  const [itemMaster, setItemMaster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [locationFilter, setLocationFilter] = useState("all");
  const [viewMode, setViewMode] = useState("card"); // 'card' or 'list'
  const [unitMode, setUnitMode] = useState("units"); // 'units' or 'boxes' - NEW!
  
  // Modal states
  const [activeAction, setActiveAction] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [actionQty, setActionQty] = useState(1);
  const [actionNotes, setActionNotes] = useState("");
  const [actionLocation, setActionLocation] = useState("Stock Room");
  const [customLocation, setCustomLocation] = useState("");
  const [transferBranch, setTransferBranch] = useState("");
  const [recountNewQty, setRecountNewQty] = useState(0);
  const [recountNewLocation, setRecountNewLocation] = useState("");
  const [recountSourceLocation, setRecountSourceLocation] = useState(""); // Which location to recount
  const [processing, setProcessing] = useState(false);
  
  // Dates for actions
  const [receiveDate, setReceiveDate] = useState(new Date().toISOString().split('T')[0]);
  const [recountDate, setRecountDate] = useState(new Date().toISOString().split('T')[0]);
  const [cargoNumber, setCargoNumber] = useState(""); // Cargo # for FIFO tracking
  const [receiveSource, setReceiveSource] = useState(""); // ANTI-THEFT: Required source/reason for Receive Stock
  const [pullOutReason, setPullOutReason] = useState(""); // ANTI-THEFT: Required reason for Pull Out (internal movement)
  const [transferReason, setTransferReason] = useState(""); // ANTI-THEFT: Required reason for inter-branch Transfer
  
  // Pull Out source/destination locations
  const [sourceLocation, setSourceLocation] = useState("");
  const [destinationLocation, setDestinationLocation] = useState("");
  const [pullOutDate, setPullOutDate] = useState(new Date().toISOString().split('T')[0]);
  
  // For new item receive
  const [newItemSearch, setNewItemSearch] = useState("");
  const [filteredNewItems, setFilteredNewItems] = useState([]);
  const [newItem, setNewItem] = useState(null);
  
  // Activity history
  const [showHistory, setShowHistory] = useState(false);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logToDelete, setLogToDelete] = useState(null); // For delete confirmation modal
  
  // Item management
  const [showItemManager, setShowItemManager] = useState(false);
  const [allItems, setAllItems] = useState([]);
  const [itemSearchQuery, setItemSearchQuery] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  
  // Pending transfers
  const [pendingTransfers, setPendingTransfers] = useState([]);
  const [showPendingTransfers, setShowPendingTransfers] = useState(false);
  const [outgoingPending, setOutgoingPending] = useState([]);
  const [showOutgoingPending, setShowOutgoingPending] = useState(false);
  const [acceptingTransfer, setAcceptingTransfer] = useState(null);
  const [acceptLocation, setAcceptLocation] = useState("Display Area");
  const [acceptReceivedQty, setAcceptReceivedQty] = useState("");
  const [acceptVarianceReason, setAcceptVarianceReason] = useState("");
  const [rejectingTransfer, setRejectingTransfer] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  
  // Item requests from other branches (where this branch is source)
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [showIncomingRequests, setShowIncomingRequests] = useState(false);
  const [approvingRequest, setApprovingRequest] = useState(null);
  const [approveLocation, setApproveLocation] = useState("Display Area");
  const [rejectingRequest, setRejectingRequest] = useState(null);
  const [requestRejectReason, setRequestRejectReason] = useState("");
  
  // Outgoing requests (where this branch is destination - MY requests to other branches)
  const [outgoingRequests, setOutgoingRequests] = useState([]);
  const [showOutgoingRequests, setShowOutgoingRequests] = useState(false);
  
  // Pending recounts for admin approval
  const [pendingRecounts, setPendingRecounts] = useState([]);
  const [showPendingRecounts, setShowPendingRecounts] = useState(false);
  
  // Remarks for stock changes
  const [changeRemarks, setChangeRemarks] = useState("");
  
  // Dynamic locations (for VZ branch)
  const [branchLocations, setBranchLocations] = useState([]);
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [newLocationName, setNewLocationName] = useState("");
  
  // Edit location modal
  const [editingLocationItem, setEditingLocationItem] = useState(null);
  const [editingCurrentLocation, setEditingCurrentLocation] = useState(""); // Track which location is being edited
  const [newLocation, setNewLocation] = useState("");
  const [editCustomLocation, setEditCustomLocation] = useState(""); // For adding new custom location
  const [savingLocation, setSavingLocation] = useState(false);
  
  // Edit box size modal
  const [editingBoxItem, setEditingBoxItem] = useState(null);
  const [newSetsPerBox, setNewSetsPerBox] = useState(1);
  const [savingBoxSize, setSavingBoxSize] = useState(false);
  
  // Item detail popover
  const [showItemDetail, setShowItemDetail] = useState(null);
  const [itemHistory, setItemHistory] = useState({}); // Cache for item history
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Full Item History Modal
  const [showFullHistory, setShowFullHistory] = useState(false);
  const [fullHistoryItem, setFullHistoryItem] = useState(null);
  
  // Add new item modal
  const [showAddItem, setShowAddItem] = useState(false);
  const [newItemName, setNewItemName] = useState("");
  const [newItemCategory, setNewItemCategory] = useState("");
  const [addingItem, setAddingItem] = useState(false);
  
  // Delete item modal
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deletingItem, setDeletingItem] = useState(false);

  // Add new item to item master
  const addNewItem = async () => {
    if (!newItemName.trim()) {
      toast.error("Item name is required");
      return;
    }
    
    setAddingItem(true);
    try {
      const response = await axios.post(`${API}/item-master/add-item`, {
        item_name: newItemName.trim(),
        category: newItemCategory.trim() || "General"
      });
      
      toast.success(`✅ Item added: ${newItemName}`);
      setNewItemName("");
      setNewItemCategory("");
      setShowAddItem(false);
      fetchAllItems(); // Refresh item list
      fetchItemMaster(); // Refresh autocomplete data
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add item");
    } finally {
      setAddingItem(false);
    }
  };

  // Delete inventory item with undo capability
  const [deleteReason, setDeleteReason] = useState("");

  const deleteInventoryItem = async () => {
    if (!itemToDelete) return;

    // ANTI-THEFT: Only admins can delete inventory records.
    if (!isAdmin()) {
      toast.error("Admin lang ang pwedeng mag-delete ng inventory records. Anti-fraud measure.");
      return;
    }

    // ANTI-THEFT: Mandatory reason for deletion (audit trail).
    if (!deleteReason || deleteReason.trim().length < 10) {
      toast.error("Please provide a reason for deletion (min 10 chars). All deletions are audited.");
      return;
    }

    setDeletingItem(true);
    try {
      const token = localStorage.getItem("auth_token");

      // Soft delete the item (can be undone within 15 minutes)
      const response = await axios.delete(
        `${API}/inventory-records/delete`,
        {
          headers: { Authorization: `Bearer ${token}` },
          data: {
            branch: selectedBranch,
            item_name: itemToDelete.item_name,
            location: itemToDelete.location,
            reason: deleteReason.trim(),
            encoder: user?.name || user?.email || "Admin"
          }
        }
      );
      
      const deletionId = response.data.deletion_id;
      const itemName = itemToDelete.item_name;
      const location = itemToDelete.location;

      // Log to movement_logs for full audit trail.
      try {
        await axios.post(`${API}/movement-logs`, {
          action_type: "DELETE_RECORD",
          branch: selectedBranch,
          item_name: itemName,
          quantity: itemToDelete.total_qty || 0,
          stock_change: -(itemToDelete.total_qty || 0),
          location: location,
          notes: `[DELETE] ${deleteReason.trim()}`,
          encoder: user?.name || user?.email || "Admin",
          timestamp: new Date().toISOString()
        });
      } catch (logErr) {
        console.warn("Could not log delete movement:", logErr);
      }

      // Show toast with Undo button
      toast.success(`🗑️ Deleted: ${itemName} from ${location}`, {
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              const undoResponse = await axios.post(
                `${API}/inventory-records/undo-delete/${deletionId}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
              );
              toast.success(undoResponse.data.message);
              await fetchInventory(); // Refresh to show restored item
            } catch (undoError) {
              toast.error(undoError.response?.data?.detail || "Failed to undo delete");
            }
          }
        },
        duration: 15000 // 15 seconds to show undo option
      });
      
      setItemToDelete(null);
      setDeleteReason(""); // Reset reason
      await fetchInventory(); // Refresh inventory
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete item");
    } finally {
      setDeletingItem(false);
    }
  };

  // Open edit location modal - now accepts specific location being edited
  const openEditLocation = (item, currentLocation) => {
    setEditingLocationItem(item);
    setEditingCurrentLocation(currentLocation || "Stock Room");
    setNewLocation(currentLocation || "Stock Room");
  };

  // Save new location for item - now correctly targets the specific location's rows
  const saveItemLocation = async () => {
    if (!editingLocationItem || !newLocation || !editingCurrentLocation) return;
    
    // Determine the final location (either selected or custom)
    let finalLocation = newLocation;
    if (newLocation === "__add_new__") {
      if (!editCustomLocation.trim()) {
        toast.error("Please enter a location name");
        return;
      }
      finalLocation = editCustomLocation.trim();
    }
    
    if (finalLocation === editingCurrentLocation) {
      toast.info("Location unchanged");
      setEditingLocationItem(null);
      setEditCustomLocation("");
      return;
    }
    
    setSavingLocation(true);
    try {
      const token = localStorage.getItem("auth_token");
      
      // If custom location, save it to the branch locations first
      if (newLocation === "__add_new__") {
        await addCustomLocationToDB(finalLocation);
      }
      
      // Find ALL rows that match the current location being edited
      const rowsToUpdate = editingLocationItem.rows.filter(r => 
        r.location === editingCurrentLocation && (r.stocks_qty > 0 || r.quantity > 0)
      );
      
      if (rowsToUpdate.length === 0) {
        toast.error("No stock found at this location to move");
        setSavingLocation(false);
        return;
      }
      
      // Update all matching rows to the new location
      const updatedRows = rowsToUpdate.map(row => ({
        ...row,
        location: finalLocation
      }));
      
      await axios.post(
        `${API}/branch-stocks/${selectedBranch}/save`,
        {
          rows: updatedRows,
          encoder: user?.email || "System"
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // ANTI-THEFT: Log location change for audit trail.
      try {
        const totalMoved = rowsToUpdate.reduce((s, r) => s + (r.stocks_qty || r.quantity || 0), 0);
        await axios.post(`${API}/movement-logs`, {
          action_type: "LOCATION_CHANGE",
          branch: selectedBranch,
          item_name: editingLocationItem.item_name,
          quantity: totalMoved,
          from_location: editingCurrentLocation,
          to_location: finalLocation,
          location: editingCurrentLocation,
          notes: `Moved ${totalMoved} units: ${editingCurrentLocation} → ${finalLocation}`,
          encoder: user?.name || user?.email || "System",
          timestamp: new Date().toISOString()
        });
      } catch (logErr) {
        console.warn("Could not log location change:", logErr);
      }

      toast.success(`✅ Moved ${rowsToUpdate.length} record(s): ${editingCurrentLocation} → ${finalLocation}`);
      setEditingLocationItem(null);
      setEditingCurrentLocation("");
      setEditCustomLocation("");
      await fetchInventory();
      await fetchBranchLocations(); // Refresh locations list
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update location");
    } finally {
      setSavingLocation(false);
    }
  };

  // Save box size (sets_per_box) for an item
  const saveBoxSize = async () => {
    if (!editingBoxItem || !newSetsPerBox) return;
    
    setSavingBoxSize(true);
    try {
      const token = localStorage.getItem("auth_token");
      
      // Update item master with new sets_per_box
      await axios.put(
        `${API}/item-master/update-box-size`,
        {
          item_name: editingBoxItem.item_name,
          sets_per_box: parseInt(newSetsPerBox)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`✅ Updated: ${editingBoxItem.item_name} = ${newSetsPerBox} units/box`);
      setEditingBoxItem(null);
      setNewSetsPerBox(1);
      await fetchItemMaster(); // Refresh item master
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update box size");
    } finally {
      setSavingBoxSize(false);
    }
  };

  // Fetch branch-specific locations from API
  const fetchBranchLocations = async () => {
    try {
      const response = await axios.get(`${API}/inventory-tracker/locations/${selectedBranch}`);
      setBranchLocations(response.data.locations || getLocationsForBranch(selectedBranch));
    } catch (error) {
      console.error("Error fetching branch locations:", error);
      setBranchLocations(getLocationsForBranch(selectedBranch));
    }
  };

  // Fetch pending transfers
  const fetchPendingTransfers = async () => {
    try {
      const response = await axios.get(`${API}/branch-stocks/${selectedBranch}/pending-transfers`);
      setPendingTransfers(response.data.pending_transfers || []);
    } catch (error) {
      console.error("Error fetching pending transfers:", error);
    }
    try {
      const outResp = await axios.get(`${API}/branch-stocks/${selectedBranch}/outgoing-pending`);
      setOutgoingPending(outResp.data.outgoing_pending || []);
    } catch (error) {
      console.error("Error fetching outgoing pending:", error);
    }
  };

  // Fetch incoming item requests (where this branch is the source)
  const fetchIncomingRequests = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.get(`${API}/item-requests`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { branch: selectedBranch, status: "PENDING", direction: "incoming" }
      });
      setIncomingRequests(response.data.requests || []);
    } catch (error) {
      console.error("Error fetching incoming requests:", error);
    }
  };
  
  // Fetch outgoing item requests (MY requests to other branches)
  const fetchOutgoingRequests = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.get(`${API}/item-requests`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { branch: selectedBranch, status: "PENDING", direction: "outgoing" }
      });
      setOutgoingRequests(response.data.requests || []);
    } catch (error) {
      console.error("Error fetching outgoing requests:", error);
    }
  };
  
  // Fetch pending recounts (for admin approval)
  const fetchPendingRecounts = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.get(`${API}/recount/pending`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { branch: selectedBranch }
      });
      setPendingRecounts(response.data.pending_recounts || []);
    } catch (error) {
      console.error("Error fetching pending recounts:", error);
    }
  };

  // Approve item request
  const handleApproveRequest = async () => {
    if (!approvingRequest || !approveLocation) return;
    
    try {
      const token = localStorage.getItem("auth_token");
      await axios.put(`${API}/item-requests/${approvingRequest.request_id}/approve`, {
        source_location: approveLocation
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`✅ Request approved! Stock deducted from ${approveLocation}`);
      setApprovingRequest(null);
      setApproveLocation("Display Area");
      fetchIncomingRequests();
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve request");
    }
  };

  // Reject item request
  const handleRejectRequest = async () => {
    // Capture the request ID FIRST before any state changes (prevents stale-state crashes)
    const currentRequest = rejectingRequest;
    if (!currentRequest) {
      toast.error("No request selected");
      return;
    }
    
    const requestId = currentRequest.request_id || currentRequest._id;
    if (!requestId) {
      toast.error("Invalid request - missing request ID");
      setRejectingRequest(null);
      return;
    }
    
    setProcessing(true);
    try {
      const token = localStorage.getItem("auth_token");
      await axios.put(`${API}/item-requests/${requestId}/reject`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { reason: requestRejectReason || "" }
      });
      
      // Close modal FIRST, then refresh (prevents stale-state render crashes)
      setRejectingRequest(null);
      setRequestRejectReason("");
      toast.success("Request rejected successfully");
      
      // Refresh AFTER modal closes — wrap in try so refresh failure doesn't whitescreen
      try {
        await fetchIncomingRequests();
      } catch (refreshErr) {
        console.error("Refresh after reject failed:", refreshErr);
      }
    } catch (error) {
      console.error("Error rejecting request:", error);
      toast.error(error.response?.data?.detail || "Failed to reject request");
      // Don't close modal on error so user can retry
    } finally {
      setProcessing(false);
    }
  };

  // Fetch inventory for branch
  const fetchInventory = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/branch-stocks/${selectedBranch}`);
      
      // Aggregate by item name ONLY (combined view - easier to understand)
      const itemMap = {};
      (response.data.rows || []).forEach(row => {
        // Skip rows that are outbound transfers or pull outs
        if (row.category?.startsWith("TRANSFER TO") || row.category === "PULL OUT") return;
        if (row.category === "TRANSFER_PENDING") return; // Skip pending transfers (old format)
        
        // Skip rows with PENDING transfer status (they show in Incoming Transfers instead)
        if (row.transfer_status === "PENDING") return;
        
        // KEY: Group by item_name only for combined view
        const key = row.item_name;
        const location = row.location || "Stock Room";
        
        if (!itemMap[key]) {
          itemMap[key] = {
            item_name: row.item_name,
            item_code: row.item_code || "",
            category: row.category || "BOXES",
            total_qty: 0,
            last_received: row.date_received,
            rows: [],
            // Location breakdown
            locations: {}
          };
        }
        
        // Add to total - only count rows with stock > 0
        const qty = row.stocks_qty || row.quantity || 0;
        if (qty > 0) {
          // CRITICAL: Tracking locations (Warranty - Claim/Defective) are AUDIT records,
          // NOT real available stock. Exclude from total_qty but still show in breakdown.
          const isTracking = isTrackingLocation(location);
          if (!isTracking) {
            itemMap[key].total_qty += qty;
          }
          itemMap[key].rows.push(row);
          
          // Track per-location breakdown (still show for transparency)
          if (!itemMap[key].locations[location]) {
            itemMap[key].locations[location] = 0;
          }
          itemMap[key].locations[location] += qty;
        }
        
        if (row.date_received > itemMap[key].last_received) {
          itemMap[key].last_received = row.date_received;
        }
      });
      
      // Sort by item name
      setInventory(Object.values(itemMap).sort((a, b) => a.item_name.localeCompare(b.item_name)));
    } catch (error) {
      console.error("Error fetching inventory:", error);
      toast.error("Failed to load inventory");
    } finally {
      setLoading(false);
    }
  }, [selectedBranch]);

  // Fetch item master list
  const fetchItemMaster = async () => {
    try {
      const response = await axios.get(`${API}/item-master/list`);
      setItemMaster(response.data.items || []);
    } catch (error) {
      console.error("Error fetching item master:", error);
    }
  };

  const [historyLookbackDays, setHistoryLookbackDays] = useState(90);

  // Fetch history for a specific item
  const fetchItemHistory = async (itemName, fetchAll = false) => {
    // Check if already cached (unless requesting all)
    if (!fetchAll && itemHistory[itemName]) return;
    
    setLoadingHistory(true);
    try {
      const token = localStorage.getItem("auth_token");
      // Calculate date range based on user-configurable lookback
      const days = fetchAll ? historyLookbackDays : 30;
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      const startStr = startDate.toISOString().split('T')[0];
      
      const response = await axios.get(`${API}/movement-logs`, {
        params: { 
          branch: selectedBranch, 
          item_name: itemName,
          start_date: startStr,
          limit: fetchAll ? 1000 : 50  // Increased from 500/20 to 1000/50
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setItemHistory(prev => ({
        ...prev,
        [itemName]: response.data.logs || []
      }));
    } catch (error) {
      console.error("Error fetching item history:", error);
      setItemHistory(prev => ({
        ...prev,
        [itemName]: []
      }));
    } finally {
      setLoadingHistory(false);
    }
  };

  // Fetch activity logs — extended lookback configurable by user (default 30 days)
  const [activityLookbackDays, setActivityLookbackDays] = useState(30);
  
  const fetchActivityLogs = async () => {
    setLoadingLogs(true);
    try {
      const token = localStorage.getItem("auth_token");
      // Calculate start date based on user's lookback choice
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - activityLookbackDays);
      const startStr = startDate.toISOString().split('T')[0];
      
      const response = await axios.get(`${API}/movement-logs`, {
        params: { 
          branch: selectedBranch, 
          start_date: startStr,
          limit: 500  // Increased from 50 to 500
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      setActivityLogs(response.data.logs || []);
    } catch (error) {
      console.error("Error fetching logs:", error);
    } finally {
      setLoadingLogs(false);
    }
  };

  // Admin function to fix fractional quantities in non-Display Area
  const fixFractionalQuantities = async () => {
    if (!window.confirm(`Ito ay mag-ro-round ng lahat ng fractional quantities sa non-Display Area locations para sa ${selectedBranch} branch.\n\nExample:\n- Inside Room: 21.5 → 22\n- Back Area D: 4.5 → 5\n\nDisplay Area ay HINDI maa-affect (pwede pa rin 0.5).\n\nContinue?`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.post(`${API}/admin/fix-fractional-quantities`, null, {
        params: { branch: selectedBranch },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.fixed_count > 0) {
        toast.success(`✅ Fixed ${response.data.fixed_count} records with fractional quantities!`);
        // Refresh inventory
        fetchInventory();
      } else {
        toast.info("No fractional quantities found to fix in non-Display Area locations");
      }
    } catch (error) {
      console.error("Error fixing fractional quantities:", error);
      toast.error(error.response?.data?.detail || "Failed to fix fractional quantities");
    }
  };

  // Admin function to REVERSE Auto-Reconcile damage
  const [isReversing, setIsReversing] = useState(false);
  const reverseAutoReconcile = async () => {
    if (!window.confirm(`⚠️ REVERSE AUTO-RECONCILE\n\nIto ay mag-aalis ng mga MALI na dagdag sa stock na ginawa ng Auto-Reconcile feature.\n\nLahat ng items na may "AUTO RECONCILE" sa history ay babalik sa DATING bilang.\n\nBranch: ${selectedBranch}\n\nContinue?`)) {
      return;
    }
    
    setIsReversing(true);
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.post(`${API}/admin/reverse-auto-reconcile`, null, {
        params: { branch: selectedBranch },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.reversed_count > 0) {
        toast.success(`✅ Na-reverse ang ${response.data.reversed_count} AUTO_RECONCILE entries! Stock restored.`);
        fetchInventory();
      } else {
        toast.info("Walang AUTO_RECONCILE entries na ma-reverse sa branch na ito");
      }
      
      if (response.data.errors && response.data.errors.length > 0) {
        console.warn("Reversal errors:", response.data.errors);
      }
    } catch (error) {
      console.error("Error reversing auto-reconcile:", error);
      toast.error(error.response?.data?.detail || "Failed to reverse auto-reconcile");
    } finally {
      setIsReversing(false);
    }
  };

  // Fetch all items for management
  const fetchAllItems = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      // Use list-all endpoint to get ALL items including INACTIVE
      const response = await axios.get(`${API}/item-master/list-all`);
      setAllItems(response.data.items || []);
    } catch (error) {
      console.error("Error fetching all items:", error);
      // Fallback to regular list endpoint
      try {
        const response = await axios.get(`${API}/item-master/list`);
        setAllItems(response.data.items || []);
      } catch (e) {
        console.error("Fallback also failed:", e);
      }
    }
  };

  // Toggle item status
  const toggleItemStatus = async (item) => {
    try {
      const token = localStorage.getItem("auth_token");
      const newStatus = item.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
      const response = await axios.put(
        `${API}/admin/item-master/${item.id}/status?status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchAllItems();
      fetchItemMaster();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    }
  };

  // Delete movement log with confirmation
  const confirmDeleteLog = (log) => {
    setLogToDelete(log);
  };
  
  const executeDeleteLog = async () => {
    if (!logToDelete) return;
    
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.delete(`${API}/movement-logs/${logToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      setLogToDelete(null);
      await fetchActivityLogs();
      await fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Delete failed");
    }
  };

  // Bulk disable test items
  const disableTestItems = async () => {
    if (!window.confirm("Disable all items starting with 'TEST_'?")) return;
    
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.post(
        `${API}/inventory-items/bulk-disable?pattern=TEST_*`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchAllItems();
      fetchItemMaster();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to disable items");
    }
  };

  useEffect(() => {
    fetchInventory();
    fetchPendingTransfers();
    fetchIncomingRequests();
    fetchOutgoingRequests();
    fetchPendingRecounts();
    fetchBranchLocations();
  }, [fetchInventory, selectedBranch]);

  useEffect(() => {
    fetchItemMaster();
  }, []);

  // Filter new items for receive modal
  useEffect(() => {
    if (!newItemSearch) {
      setFilteredNewItems([]);
    } else {
      const query = newItemSearch.toLowerCase();
      setFilteredNewItems(
        itemMaster.filter(item => 
          item.item_name?.toLowerCase().includes(query)
        ).slice(0, 15)
      );
    }
  }, [newItemSearch, itemMaster]);

  // Accept pending transfer with location
  const acceptPendingTransfer = async () => {
    if (!acceptingTransfer || !acceptLocation) return;
    
    // ANTI-THEFT: Variance validation
    const sentQty = acceptingTransfer.stocks_qty || acceptingTransfer.quantity || 0;
    const receivedQty = acceptReceivedQty === "" ? sentQty : Number(acceptReceivedQty);
    if (isNaN(receivedQty) || receivedQty < 0 || receivedQty > sentQty) {
      toast.error(`Invalid received quantity! Dapat 0 hanggang ${sentQty} lang.`);
      return;
    }
    if (receivedQty < sentQty && acceptVarianceReason.trim().length < 5) {
      toast.error("May kulang sa natanggap — ilagay ang dahilan (min 5 characters). Ma-fa-flag ito bilang variance.");
      return;
    }
    
    setProcessing(true);
    try {
      const token = localStorage.getItem("auth_token");
      // Use row_id if available, otherwise fall back to id
      const transferId = acceptingTransfer.row_id || acceptingTransfer.id;
      const response = await axios.post(
        `${API}/branch-stocks/${selectedBranch}/accept-transfer`,
        { row_id: transferId, location: acceptLocation, received_qty: receivedQty, variance_reason: acceptVarianceReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.variance_qty > 0) {
        toast.warning(response.data.message, { duration: 8000 });
      } else {
        toast.success(response.data.message);
      }
      setAcceptingTransfer(null);
      setAcceptLocation("Display Area");
      setAcceptReceivedQty("");
      setAcceptVarianceReason("");
      setShowPendingTransfers(false);
      await fetchPendingTransfers();
      await fetchInventory();
    } catch (error) {
      console.error("Accept transfer error:", error);
      toast.error(error.response?.data?.detail || "Failed to accept transfer");
    } finally {
      setProcessing(false);
    }
  };

  // Reject a pending transfer - returns stock to source branch
  const rejectPendingTransfer = async () => {
    // Snapshot BEFORE async ops to prevent stale-state crashes
    const currentTransfer = rejectingTransfer;
    if (!currentTransfer) {
      toast.error("No transfer selected");
      return;
    }
    
    // Rejection reason is MANDATORY (audited; stock returns to sender)
    if (!rejectReason || rejectReason.trim().length < 5) {
      toast.error("Kailangan ng dahilan sa pag-reject (min 5 characters). Babalik ang stock sa sender.");
      return;
    }
    
    // Use row_id first (newer schema), fall back to id (legacy)
    const transferId = currentTransfer.row_id || currentTransfer.id;
    if (!transferId) {
      toast.error("Transfer has no valid ID — cannot reject. Refresh and try again.");
      setRejectingTransfer(null);
      return;
    }
    
    setProcessing(true);
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.post(
        `${API}/branch-stocks/${selectedBranch}/reject-transfer`,
        null,
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: { 
            row_id: transferId, 
            reason: rejectReason.trim() 
          }
        }
      );
      
      // Close modal FIRST, then refresh — prevents stale-state crash during re-render
      setRejectingTransfer(null);
      setRejectReason("");
      toast.success(response?.data?.message || "Transfer rejected successfully");
      
      // Wrap refreshes so a refresh failure doesn't whitescreen
      try {
        await fetchPendingTransfers();
      } catch (e) {
        console.error("Refresh pending transfers failed:", e);
      }
      try {
        await fetchInventory();
      } catch (e) {
        console.error("Refresh inventory failed:", e);
      }
    } catch (error) {
      console.error("Reject transfer error:", error);
      toast.error(error?.response?.data?.detail || "Failed to reject transfer");
      // Don't close modal on error so user can retry
    } finally {
      setProcessing(false);
    }
  };

  // Add custom location (for VZ branch)
  const addCustomLocation = async () => {
    if (!newLocationName.trim()) return;
    
    try {
      const response = await axios.post(`${API}/inventory-tracker/locations`, {
        branch: selectedBranch,
        location_name: newLocationName.trim()
      });
      toast.success(response.data.message);
      setNewLocationName("");
      setShowAddLocation(false);
      fetchBranchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add location");
    }
  };

  // Get stock status
  const getStockStatus = (qty) => {
    if (qty <= 0) return { label: "OUT", color: "bg-red-100 text-red-700 border-red-300", icon: XCircle };
    if (qty <= 5) return { label: "CRITICAL", color: "bg-red-100 text-red-700 border-red-300", icon: AlertTriangle };
    if (qty <= 10) return { label: "LOW", color: "bg-orange-100 text-orange-700 border-orange-300", icon: AlertTriangle };
    return { label: "OK", color: "bg-green-100 text-green-700 border-green-300", icon: CheckCircle };
  };

  // Get box info for an item from item master
  const getBoxInfo = (itemName) => {
    const masterItem = itemMaster.find(m => m.item_name === itemName);
    if (masterItem && masterItem.sets_per_box && masterItem.sets_per_box > 1) {
      return {
        setsPerBox: masterItem.sets_per_box,
        packagingType: masterItem.packaging_type || "Box"
      };
    }
    return null;
  };

  // Format quantity based on unitMode (units or boxes)
  const formatQuantity = (qty, itemName) => {
    if (unitMode === "units") {
      return { value: qty, label: "units", raw: qty };
    }
    
    const boxInfo = getBoxInfo(itemName);
    if (!boxInfo) {
      return { value: qty, label: "units", raw: qty };
    }
    
    const boxes = Math.floor(qty / boxInfo.setsPerBox);
    const loose = qty % boxInfo.setsPerBox;
    
    return {
      value: boxes,
      loose: loose,
      label: boxes === 1 ? "box" : "boxes",
      setsPerBox: boxInfo.setsPerBox,
      raw: qty,
      hasLoose: loose > 0
    };
  };

  // Get ALL branch locations with item/qty counts for the filter dropdown
  const locationStats = useMemo(() => {
    // Start with all predefined branch locations
    const allBranchLocs = branchLocations.length > 0 ? branchLocations : getLocationsForBranch(selectedBranch);
    
    // Calculate stats for each location
    const stats = {};
    allBranchLocs.forEach(loc => {
      stats[loc] = { items: 0, qty: 0 };
    });
    
    // Count items and quantities per location from inventory
    inventory.forEach(item => {
      if (item.locations) {
        Object.entries(item.locations).forEach(([loc, qty]) => {
          if (qty > 0) {
            if (!stats[loc]) {
              stats[loc] = { items: 0, qty: 0 };
            }
            stats[loc].items += 1;
            stats[loc].qty += qty;
          }
        });
      }
    });
    
    // Convert to sorted array with all locations
    return Object.entries(stats)
      .map(([loc, data]) => ({ location: loc, items: data.items, qty: data.qty }))
      .sort((a, b) => a.location.localeCompare(b.location));
  }, [inventory, branchLocations, selectedBranch]);

  // Filter inventory - now works with combined view locations
  // Memoized to avoid re-computing on every render (esp. with 1000+ items)
  const filteredInventory = useMemo(() => {
    const searchLower = searchQuery.toLowerCase();
    return inventory.filter(item => {
      const matchesSearch = !searchQuery || 
        item.item_name.toLowerCase().includes(searchLower) ||
        item.item_code?.toLowerCase().includes(searchLower);
      
      // For location filter, check if item has stock at that location
      const matchesLocation = locationFilter === "all" || 
        (item.locations && item.locations[locationFilter] > 0);
      
      if (statusFilter === "all") return matchesSearch && matchesLocation;
      
      const status = getStockStatus(item.total_qty);
      if (statusFilter === "ok") return matchesSearch && matchesLocation && status.label === "OK";
      if (statusFilter === "low") return matchesSearch && matchesLocation && (status.label === "LOW" || status.label === "CRITICAL");
      if (statusFilter === "out") return matchesSearch && matchesLocation && status.label === "OUT";
      return matchesSearch && matchesLocation;
    });
  }, [inventory, searchQuery, locationFilter, statusFilter]);

  // Stats — memoized to avoid 4 redundant .filter() passes on every render
  const stats = useMemo(() => ({
    total: inventory.length,
    totalQty: inventory.reduce((sum, i) => sum + i.total_qty, 0),
    ok: inventory.filter(i => getStockStatus(i.total_qty).label === "OK").length,
    low: inventory.filter(i => ["LOW", "CRITICAL"].includes(getStockStatus(i.total_qty).label)).length,
    out: inventory.filter(i => getStockStatus(i.total_qty).label === "OUT").length,
    pendingTransfers: pendingTransfers.length
  }), [inventory, pendingTransfers]);

  // Export stocks CSV (authenticated fetch → blob download; direct window.open fails with 401)
  const handleExportStocks = async () => {
    try {
      const response = await axios.get(`${API}/export/stocks/${selectedBranch}?format=csv`, {
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${selectedBranch}_stocks_recount_${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`✅ Na-export ang ${selectedBranch} stocks CSV`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Hindi na-export ang stocks. Subukan ulit.");
    }
  };

  // Open action modal
  const openAction = (action, item = null) => {
    setActiveAction(action);
    setSelectedItem(item);
    setActionQty(1);
    setActionNotes("");
    setActionLocation(item?.location || "Display Area");
    setCustomLocation("");
    setTransferBranch("");
    // For RECOUNT: Get the first SELLABLE location with stock (skip tracking-only warranty locations)
    const sellableLocations = item?.locations ? Object.keys(item.locations).filter(loc => !isTrackingLocation(loc)) : [];
    const firstLocationWithStock = sellableLocations[0] || "Display Area";
    const firstLocationQty = item?.locations?.[firstLocationWithStock] || item?.total_qty || 0;
    setRecountSourceLocation(firstLocationWithStock);
    setRecountNewQty(firstLocationQty);
    setRecountNewLocation(firstLocationWithStock); // Default to same location
    // For Pull Out: source is current location, destination defaults to Display Area
    // For Transfer: sourceLocation should be EMPTY so user must select
    setSourceLocation(action === "TRANSFER" ? "" : (item?.location || "Stock Room"));
    setDestinationLocation("Display Area");
    setPullOutDate(new Date().toISOString().split('T')[0]); // Reset to today
    setReceiveDate(new Date().toISOString().split('T')[0]); // Reset to today
    setCargoNumber(""); // Reset cargo number
    setReceiveSource(""); // Reset receive source (mandatory anti-theft)
    setPullOutReason(""); // Reset pull-out reason
    setTransferReason(""); // Reset transfer reason
    setRecountDate(new Date().toISOString().split('T')[0]); // Reset to today
    setNewItem(null);
    setNewItemSearch("");
  };

  // Close modal
  const closeModal = () => {
    setActiveAction(null);
    setSelectedItem(null);
    setNewItem(null);
    setNewItemSearch("");
    setSourceLocation("");
    setDestinationLocation("");
  };

  // Get final location value (for RECEIVE action)
  const getFinalLocation = () => {
    if (actionLocation === "custom" && customLocation.trim()) {
      return customLocation.trim();
    }
    return actionLocation;
  };
  
  // Get final destination location (for PULL_OUT and RECOUNT actions)
  const getFinalDestinationLocation = () => {
    if (destinationLocation === "custom" && customLocation.trim()) {
      return customLocation.trim();
    }
    return destinationLocation;
  };
  
  // Get final recount location
  const getFinalRecountLocation = () => {
    if (recountNewLocation === "custom" && customLocation.trim()) {
      return customLocation.trim();
    }
    return recountNewLocation;
  };
  
  // Add custom location to database
  const addCustomLocationToDB = async (locationName) => {
    try {
      await axios.post(`${API}/inventory-tracker/locations`, {
        branch: selectedBranch,
        location_name: locationName
      });
      // Refresh locations list
      fetchBranchLocations();
    } catch (error) {
      // Ignore if already exists
      console.log("Location may already exist:", error.response?.data?.detail);
    }
  };

  // Execute action
  const executeAction = async () => {
    if (activeAction === "RECEIVE" && !selectedItem && !newItem) {
      toast.error("Please select an item");
      return;
    }
    
    if (activeAction !== "RECEIVE" && !selectedItem) {
      toast.error("No item selected");
      return;
    }
    
    if (activeAction === "TRANSFER" && !transferBranch) {
      toast.error("Please select destination branch");
      return;
    }
    
    if (activeAction === "RECEIVE" && !getFinalLocation()) {
      toast.error("Please select a location");
      return;
    }

    // ANTI-THEFT: Mandatory source/reason for Receive Stock to prevent fake inventory inflation.
    if (activeAction === "RECEIVE") {
      if (!receiveSource) {
        toast.error("Please select a source/reason for receiving stock (anti-theft requirement).");
        return;
      }
      // For "New Purchase" — cargo number is required.
      if (receiveSource === "New Purchase" && !cargoNumber.trim()) {
        toast.error("Cargo # is required for New Purchase. Walang cargo, walang receive.");
        return;
      }
      // For "Other" — notes are required.
      if (receiveSource === "Other" && (!actionNotes || actionNotes.trim().length < 5)) {
        toast.error("Please describe the source in notes (min 5 chars) for 'Other' receive type.");
        return;
      }
    }

    // ANTI-THEFT: Mandatory reason for Pull Out (internal movement) — prevents masking theft as "moved to stock room".
    if (activeAction === "PULL_OUT") {
      if (!pullOutReason) {
        toast.error("Please select a reason for the pull out / movement (anti-theft requirement).");
        return;
      }
      if (pullOutReason === "Other" && (!actionNotes || actionNotes.trim().length < 5)) {
        toast.error("Please describe the reason in notes (min 5 chars) for 'Other' pull-out reason.");
        return;
      }
    }

    // ANTI-THEFT: Mandatory reason for inter-branch Transfer.
    if (activeAction === "TRANSFER") {
      if (!transferReason) {
        toast.error("Please select a reason for the transfer (anti-theft requirement).");
        return;
      }
      if (transferReason === "Other" && (!actionNotes || actionNotes.trim().length < 5)) {
        toast.error("Please describe the reason in notes (min 5 chars) for 'Other' transfer reason.");
        return;
      }
    }

    const itemToUse = selectedItem || newItem;
    const encoder = user?.name || user?.email?.split('@')[0] || "System";
    
    // Convert boxes to units if in boxes mode
    const boxInfo = getBoxInfo(itemToUse?.item_name);
    const isBoxMode = unitMode === 'boxes' && boxInfo;
    const actualQty = isBoxMode ? actionQty * boxInfo.setsPerBox : actionQty;
    const displayQty = isBoxMode ? `${actionQty} box${actionQty !== 1 ? 'es' : ''} (${actualQty} units)` : `${actualQty}`;
    
    setProcessing(true);
    try {
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0];
      const finalLocation = getFinalLocation();
      
      // If custom location is used, save it to database for future use
      if (actionLocation === "custom" && customLocation.trim()) {
        await addCustomLocationToDB(customLocation.trim());
      }
      if (destinationLocation === "custom" && customLocation.trim()) {
        await addCustomLocationToDB(customLocation.trim());
      }
      if (recountNewLocation === "custom" && customLocation.trim()) {
        await addCustomLocationToDB(customLocation.trim());
      }
      
      if (activeAction === "RECEIVE") {
        // Create a new inventory row for received stock with location
        await axios.post(`${API}/branch-stocks/${selectedBranch}/save`, {
          rows: [{
            id: `NEW-${Date.now()}`,
            is_new: true,
            branch: selectedBranch,
            encoder: encoder,
            date_received: receiveDate || dateStr, // Use selected date
            category: "BOXES",
            item_code: itemToUse.item_code || "",
            item_name: itemToUse.item_name,
            stocks_qty: actualQty,  // Use converted quantity
            location: finalLocation,
            cargo_number: cargoNumber || "", // Cargo # for FIFO tracking
            receive_source: receiveSource, // ANTI-THEFT: documented source
            suppress_movement_log: true, // frontend logs RECEIVE below (avoid double log)
            date_pulled_out: "",
            notes: actionNotes || `Received ${displayQty} at ${finalLocation} (${receiveDate || dateStr}) [Source: ${receiveSource}]${cargoNumber ? ` [Cargo: ${cargoNumber}]` : ''}`
          }],
          encoder: encoder,
          receive_date: receiveDate || dateStr // Send date to backend for logging
        });

        // ANTI-THEFT: Log RECEIVE in movement_logs for audit trail.
        try {
          await axios.post(`${API}/movement-logs`, {
            action_type: "RECEIVE",
            branch: selectedBranch,
            item_name: itemToUse.item_name,
            quantity: actualQty,
            location: finalLocation,
            previous_stock: 0,
            new_stock: actualQty,
            notes: `[${receiveSource}]${cargoNumber ? ` Cargo: ${cargoNumber}.` : ''} ${actionNotes || ''}`.trim(),
            receive_source: receiveSource,
            cargo_number: cargoNumber || "",
            encoder: encoder,
            timestamp: new Date().toISOString()
          });
        } catch (logErr) {
          console.warn("Could not log receive movement:", logErr);
        }

        toast.success(`✅ Received ${displayQty} ${itemToUse.item_name} at ${finalLocation} [${receiveSource}]${cargoNumber ? ` [Cargo: ${cargoNumber}]` : ''}`);
      }
      
      else if (activeAction === "PULL_OUT") {
        // PULL OUT now means internal movement: Source Location → Destination Location
        const finalDestination = getFinalDestinationLocation();
        
        if (actualQty > selectedItem.total_qty) {
          toast.error(`Hindi sapat ang stock! Available: ${selectedItem.total_qty}`);
          setProcessing(false);
          return;
        }
        
        const rowToUpdate = selectedItem.rows.find(r => r.stocks_qty > 0 && r.location === sourceLocation);
        if (!rowToUpdate) {
          // Try to find any row with stock
          const anyRow = selectedItem.rows.find(r => r.stocks_qty > 0);
          if (!anyRow) {
            toast.error(`Walang stock na available`);
            setProcessing(false);
            return;
          }
        }
        
        const sourceRow = rowToUpdate || selectedItem.rows.find(r => r.stocks_qty > 0);
        
        if (sourceRow) {
          // Deduct from source and create new row at destination
          if (actualQty < sourceRow.stocks_qty) {
            await axios.post(`${API}/branch-stocks/${selectedBranch}/save`, {
              rows: [
                { 
                  ...sourceRow, 
                  stocks_qty: sourceRow.stocks_qty - actualQty,
                  action_type: "PULL_OUT",  // Explicitly mark as PULL_OUT
                  suppress_movement_log: true, // frontend logs INTERNAL_MOVE below
                  date_pulled_out: pullOutDate
                },
                {
                  id: `NEW-${Date.now()}`,
                  is_new: true,
                  branch: selectedBranch,
                  encoder: encoder,
                  date_received: sourceRow.date_received,
                  category: "BOXES",  // Moved stock, not "PULL OUT"
                  item_code: sourceRow.item_code,
                  item_name: sourceRow.item_name,
                  stocks_qty: actualQty,
                  location: finalDestination,  // New location (can be custom)
                  suppress_movement_log: true, // internal move — not a real RECEIVE
                  date_pulled_out: pullOutDate,  // Use the selected date
                  pull_out_reason: pullOutReason,
                  notes: `[${pullOutReason}] ${actionNotes || ''} | Moved ${displayQty}: ${sourceLocation} → ${finalDestination} (${pullOutDate})`.trim()
                }
              ],
              encoder: encoder,
              pull_out_date: pullOutDate  // Send date to backend for logging
            });
          } else {
            // Move all stock from source to destination
            await axios.post(`${API}/branch-stocks/${selectedBranch}/save`, {
              rows: [{ 
                ...sourceRow, 
                location: finalDestination, 
                date_pulled_out: pullOutDate,  // Use the selected date
                pull_out_reason: pullOutReason,
                notes: `[${pullOutReason}] ${actionNotes || ''} | Moved: ${sourceLocation} → ${finalDestination} (${pullOutDate})`.trim()
              }],
              encoder: encoder,
              pull_out_date: pullOutDate  // Send date to backend for logging
            });
          }

          // ANTI-THEFT: Log internal move in movement_logs for audit trail.
          // INTERNAL_MOVE = net-zero within-branch move (NOT counted as pull-out in tally/discrepancy math)
          try {
            await axios.post(`${API}/movement-logs`, {
              action_type: "INTERNAL_MOVE",
              branch: selectedBranch,
              item_name: selectedItem.item_name,
              quantity: actualQty,
              from_location: sourceLocation,
              to_location: finalDestination,
              location: sourceLocation,
              notes: `[${pullOutReason}] ${actionNotes || ''}`.trim(),
              encoder: encoder,
              timestamp: new Date().toISOString()
            });
          } catch (logErr) {
            console.warn("Could not log pull-out movement:", logErr);
          }
        }
        toast.success(`✅ Moved ${displayQty} ${selectedItem.item_name}: ${sourceLocation} → ${finalDestination} [${pullOutReason}]`);
      }
      
      else if (activeAction === "TRANSFER") {
        // Validate source location is selected
        if (!sourceLocation) {
          toast.error("Piliin muna kung saan babawasan ng stock!");
          setProcessing(false);
          return;
        }
        
        // Get rows from the selected source location only
        const rowsFromLocation = selectedItem.rows
          .filter(r => (r.stocks_qty || r.quantity || 0) > 0 && r.location === sourceLocation);
        
        // Calculate total available in selected location
        const availableInLocation = rowsFromLocation.reduce((sum, r) => sum + (r.stocks_qty || r.quantity || 0), 0);
        
        if (actualQty > availableInLocation) {
          toast.error(`Hindi sapat ang stock sa ${sourceLocation}! Available: ${availableInLocation}`);
          setProcessing(false);
          return;
        }
        
        if (rowsFromLocation.length === 0) {
          toast.error(`Walang stock sa ${sourceLocation}`);
          setProcessing(false);
          return;
        }
        
        // Fractional (0.5) transfers allowed ONLY from Display Area — rounding elsewhere creates phantom units
        if (actualQty !== Math.floor(actualQty) && !sourceLocation.toLowerCase().startsWith("display")) {
          toast.error(`Hindi pwede ang fractional (${actualQty}) galing sa ${sourceLocation}. Display Area lang ang may fractional stock.`);
          setProcessing(false);
          return;
        }
        
        const token = localStorage.getItem("auth_token");
        const transferId = `TRF-${Date.now().toString(36).toUpperCase()}`;
        
        // Deduct from selected location only
        let remainingToTransfer = actualQty;
        const rowsToUpdate = [];
        
        for (const row of rowsFromLocation) {
          if (remainingToTransfer <= 0) break;
          
          const rowQty = row.stocks_qty || row.quantity || 0;
          const deductAmount = Math.min(rowQty, remainingToTransfer);
          
          if (deductAmount >= rowQty) {
            // Delete the entire row (or set to 0)
            rowsToUpdate.push({ 
              ...row, 
              stocks_qty: 0, 
              quantity: 0, 
              deleted: true, 
              is_deleted: true,
              suppress_movement_log: true // frontend logs TRANSFER_OUT below (avoid spurious RECOUNT log)
            });
          } else {
            // Partial deduction
            rowsToUpdate.push({ 
              ...row, 
              stocks_qty: rowQty - deductAmount, 
              quantity: rowQty - deductAmount,
              suppress_movement_log: true // frontend logs TRANSFER_OUT below (avoid spurious RECOUNT log)
            });
          }
          
          remainingToTransfer -= deductAmount;
        }
        
        // 1. Deduct from source branch (selected location only)
        if (rowsToUpdate.length > 0) {
          await axios.post(`${API}/branch-stocks/${selectedBranch}/save`, {
            rows: rowsToUpdate,
            encoder: encoder
          });
        }
        
        // 2. Create pending transfer for destination branch
        const newTransferId = `TRANSFER-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;
        const primaryRow = rowsFromLocation[0];
        await axios.post(`${API}/branch-stocks/${transferBranch}/save`, {
          rows: [{
            id: newTransferId,
            is_new: true,
            row_id: newTransferId,
            transfer_id: transferId,
            branch: transferBranch,
            encoder: encoder,
            date_received: dateStr,
            category: "TRANSFER IN",
            item_code: primaryRow.item_code || "",
            item_name: primaryRow.item_name,
            stocks_qty: actualQty,
            quantity: actualQty,
            location: null, // Location will be set when they accept
            transfer_status: "PENDING",
            source_branch: selectedBranch,
            source_location: sourceLocation,
            notes: `[${transferReason}] ${actionNotes || ''} | Transfer ${displayQty} from ${selectedBranch} (${sourceLocation})`.trim()
          }],
          encoder: encoder
        });
        
        // 3. Log the transfer
        await axios.post(`${API}/movement-logs`, {
          action_type: "TRANSFER_OUT",
          branch: selectedBranch,
          from_branch: selectedBranch,
          to_branch: transferBranch,
          transfer_id: transferId,
          item_name: primaryRow.item_name,
          quantity: actualQty,
          location: sourceLocation,
          destination_branch: transferBranch,
          encoder: encoder,
          notes: `[${transferReason}] ${actionNotes || ''} | Transfer ${displayQty} to ${transferBranch} from ${sourceLocation}`.trim()
        }, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(err => console.warn("Log error:", err));
        
        toast.success(`✅ Transferred ${displayQty} ${selectedItem.item_name} from ${sourceLocation} to ${BRANCHES[transferBranch]} [${transferReason}]`);
      }
      
      else if (activeAction === "RECOUNT") {
        // LOCATION-SPECIFIC RECOUNT: Only affect records in the selected location
        // This preserves stocks in OTHER locations
        
        if (!recountSourceLocation) {
          toast.error("Please select a location to recount");
          setProcessing(false);
          return;
        }

        // ANTI-THEFT: Mandatory reason for any quantity change to prevent stock manipulation.
        if (!actionNotes || actionNotes.trim().length < 5) {
          toast.error("Please provide a reason for this recount (min 5 chars). All recounts are audited.");
          setProcessing(false);
          return;
        }
        
        const allRows = selectedItem.rows || [];
        // Filter to only rows in the SOURCE location being recounted
        const rowsInLocation = allRows.filter(r => (r.location || "Stock Room") === recountSourceLocation);
        
        if (rowsInLocation.length === 0) {
          toast.error(`No inventory records found in ${recountSourceLocation}`);
          setProcessing(false);
          return;
        }
        
        const locationOldQty = selectedItem.locations?.[recountSourceLocation] || 0;
        const finalRecountLoc = getFinalRecountLocation() || recountSourceLocation;
        const locationChanged = finalRecountLoc !== recountSourceLocation;
        
        let recountNote = `Recount (${recountDate}): ${recountSourceLocation} ${locationOldQty} → ${recountNewQty}`;
        if (locationChanged) {
          recountNote += ` | Moved to ${finalRecountLoc}`;
        }
        if (actionNotes) {
          recountNote += ` | ${actionNotes}`;
        }
        
        // Handle custom location - add to DB if needed
        if (recountNewLocation === "custom" && customLocation.trim()) {
          await addCustomLocationToDB(customLocation.trim());
        }
        
        const primaryRow = rowsInLocation[0];
        
        // SIMPLE CONFIRMATION (replaces admin approval workflow — applies immediately for ALL users)
        const diff = recountNewQty - locationOldQty;
        const diffLabel = diff > 0 ? `+${diff}` : `${diff}`;
        const locChangeLabel = locationChanged ? ` and MOVE to "${finalRecountLoc}"` : "";
        const confirmMsg = `Confirm Recount?\n\nItem: ${selectedItem.item_name}\nLocation: ${recountSourceLocation}\nQuantity: ${locationOldQty} → ${recountNewQty} (${diffLabel})${locChangeLabel}\n\nThis will be applied IMMEDIATELY.`;
        
        if (!window.confirm(confirmMsg)) {
          setProcessing(false);
          return;
        }
        
        // ATOMIC RECOUNT: single backend call — consolidates rows, ONE audited log (delta), synced totals
        const recountResp = await axios.post(`${API}/recount/apply`, {
          branch: selectedBranch,
          item_name: selectedItem.item_name,
          source_location: recountSourceLocation,
          new_qty: recountNewQty,
          new_location: finalRecountLoc,
          encoder: encoder,
          reason: actionNotes,
          recount_date: recountDate
        });
        const rc = recountResp.data;
        
        // IMMEDIATE STATE UPDATE - recalculate totals
        setInventory(prev => prev.map(inv => {
          if (inv.item_name === selectedItem.item_name) {
            const newLocations = { ...inv.locations };
            // Remove old location count
            delete newLocations[recountSourceLocation];
            // Add new count to destination (add on top of existing stock there, if any)
            if (recountNewQty > 0) {
              newLocations[rc.new_location] = (newLocations[rc.new_location] || 0) + recountNewQty;
            }
            // Recalculate total
            const newTotal = Object.values(newLocations).reduce((sum, qty) => sum + qty, 0);
            
            // Update rows - keep rows NOT in the source location, add updated primary row
            const otherRows = inv.rows.filter(r => (r.location || "Stock Room") !== recountSourceLocation);
            const newRows = recountNewQty > 0 
              ? [...otherRows, { ...primaryRow, stocks_qty: recountNewQty, location: rc.new_location }]
              : otherRows;
            
            return {
              ...inv,
              total_qty: newTotal,
              locations: newLocations,
              rows: newRows
            };
          }
          return inv;
        }));
        
        let successMsg = `✅ Recounted ${selectedItem.item_name} in ${recountSourceLocation}: ${rc.old_qty} → ${rc.new_qty}`;
        if (rc.consolidated_rows > 0) {
          successMsg += ` (consolidated ${rc.consolidated_rows + 1} records)`;
        }
        if (locationChanged) {
          successMsg += ` | Moved to ${rc.new_location}`;
        }
        toast.success(successMsg);
      }
      
      // Close modal first, then refresh inventory in background
      closeModal();
      await fetchInventory();
    } catch (error) {
      console.error("Action error:", error);
      toast.error(error.response?.data?.detail || "Action failed");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <ErrorBoundary onReset={() => { fetchInventory(); fetchPendingTransfers(); fetchIncomingRequests(); }}>
    <div className="min-h-screen bg-gray-50" data-testid="inventory-page">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-lg bg-indigo-600 flex items-center justify-center">
              <Package className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{selectedBranch} Inventory</h1>
              <p className="text-sm text-gray-500">{BRANCHES[selectedBranch]} - Action-Based Stock Management</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button 
              onClick={() => openAction("RECEIVE")} 
              className="bg-green-600 hover:bg-green-700"
              data-testid="receive-stock-btn"
              disabled={!canEdit}
              title={!canEdit ? "You need a branch assigned to your email to edit inventory" : ""}
            >
              <Plus className="h-4 w-4 mr-2" />
              Receive Stock
            </Button>
            {/* Pending Transfers Button - shows badge if there are pending */}
            <Button 
              variant="outline"
              onClick={() => { setShowPendingTransfers(true); fetchPendingTransfers(); }}
              className={`relative ${pendingTransfers.length > 0 ? 'text-orange-600 border-orange-300 hover:bg-orange-50' : 'text-gray-600 border-gray-200 hover:bg-gray-50'}`}
              data-testid="pending-transfers-btn"
            >
              <Inbox className="h-4 w-4 mr-2" />
              Incoming
              {pendingTransfers.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-orange-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                  {pendingTransfers.length}
                </span>
              )}
            </Button>
            {/* Item Requests Button - requests from other branches */}
            <Button 
              variant="outline"
              onClick={() => { setShowIncomingRequests(true); fetchIncomingRequests(); }}
              className={`relative ${incomingRequests.length > 0 ? 'text-blue-600 border-blue-300 hover:bg-blue-50' : 'text-gray-600 border-gray-200 hover:bg-gray-50'}`}
              data-testid="incoming-requests-btn"
            >
              <ArrowRightLeft className="h-4 w-4 mr-2" />
              Requests
              {incomingRequests.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                  {incomingRequests.length}
                </span>
              )}
            </Button>
            {/* My Outgoing Requests - requests I made to other branches */}
            <Button 
              variant="outline"
              onClick={() => { setShowOutgoingRequests(true); fetchOutgoingRequests(); }}
              className={`relative ${outgoingRequests.length > 0 ? 'text-green-600 border-green-300 hover:bg-green-50' : 'text-gray-600 border-gray-200 hover:bg-gray-50'}`}
              data-testid="outgoing-requests-btn"
            >
              <ArrowRightLeft className="h-4 w-4 mr-2" />
              My Requests
              {outgoingRequests.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                  {outgoingRequests.length}
                </span>
              )}
            </Button>
            <TransferNotifications branch={selectedBranch} />
            <Button 
              variant="outline"
              onClick={() => { setShowHistory(true); fetchActivityLogs(); }}
              className="text-blue-600 border-blue-200 hover:bg-blue-50"
              data-testid="history-btn"
            >
              <History className="h-4 w-4 mr-2" />
              Activity
            </Button>
            <Button 
              variant="outline"
              onClick={() => { setShowItemManager(true); fetchAllItems(); }}
              className="text-purple-600 border-purple-200 hover:bg-purple-50"
              data-testid="item-manager-btn"
            >
              <Settings className="h-4 w-4 mr-2" />
              Items
            </Button>
            <Button 
              variant="outline"
              onClick={handleExportStocks}
              className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
              data-testid="export-stocks-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            {/* Pending Recounts - Legacy view (admin only, only shows when there are old pending entries from previous workflow) */}
            {isAdmin() && pendingRecounts.length > 0 && (
              <Button 
                variant="outline"
                onClick={() => { setShowPendingRecounts(true); fetchPendingRecounts(); }}
                className="relative text-amber-600 border-amber-300 hover:bg-amber-50"
                data-testid="pending-recounts-btn"
                title="Legacy pending recounts from previous approval workflow"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Legacy Recounts
                <span className="absolute -top-2 -right-2 bg-amber-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                  {pendingRecounts.length}
                </span>
              </Button>
            )}
            <Select value={selectedBranch} onValueChange={setSelectedBranch} disabled={!canAccessAllBranches && userBranches.length <= 1}>
              <SelectTrigger className="w-[180px]" data-testid="branch-selector">
                <SelectValue />
                {!canAccessAllBranches && (
                  <span className="ml-1 text-xs text-orange-500">🔒</span>
                )}
              </SelectTrigger>
              <SelectContent>
                {Object.entries(BRANCHES).map(([code, name]) => (
                  <SelectItem 
                    key={code} 
                    value={code}
                    disabled={!canAccessAllBranches && !userBranches.includes(code)}
                  >
                    {code} - {name}
                    {!canAccessAllBranches && userBranches.includes(code) && " ✓"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!canAccessAllBranches && (
              <span className="text-xs text-orange-600 ml-2">
                🔒 Locked to {userBranches.join(", ") || "—"}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* No Edit Permission Warning */}
      {!canEdit && (
        <div className="bg-red-50 border-b border-red-200 px-6 py-3">
          <div className="flex items-center gap-2 text-red-800">
            <AlertTriangle className="h-5 w-5" />
            <span className="font-medium">VIEW ONLY MODE - Your email does not have a branch assigned. Contact admin to enable editing.</span>
          </div>
        </div>
      )}

      {/* Pending Transfers Alert Banner */}
      {pendingTransfers.length > 0 && (
        <div className="bg-orange-50 border-b border-orange-200 px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-orange-800">
              <Inbox className="h-5 w-5" />
              <span className="font-medium">{pendingTransfers.length} incoming transfer(s) waiting for location assignment</span>
            </div>
            <Button 
              size="sm" 
              onClick={() => setShowPendingTransfers(true)}
              className="bg-orange-600 hover:bg-orange-700"
            >
              Review Now
            </Button>
          </div>
        </div>
      )}

      {/* INCOMING TRANSFER NOTIFICATION BANNER */}
      {pendingTransfers.length > 0 && (
        <div
          className="bg-amber-50 border-b-2 border-amber-300 px-6 py-3 flex items-center justify-between cursor-pointer hover:bg-amber-100 transition-colors"
          onClick={() => { setShowPendingTransfers(true); fetchPendingTransfers(); }}
          data-testid="incoming-transfer-banner"
        >
          <div className="flex items-center gap-3">
            <Inbox className="h-5 w-5 text-amber-600" />
            <div>
              <span className="font-bold text-amber-800">📦 Incoming Transfer: {pendingTransfers.length}</span>
              <span className="text-sm text-amber-700 ml-2">May stock na naghihintay i-receive. I-click para i-review (Accept / Reject).</span>
            </div>
          </div>
          <span className="text-xs font-semibold bg-amber-500 text-white px-2 py-1 rounded-full">IN TRANSIT</span>
        </div>
      )}

      {/* OUTGOING TRANSFERS NOT YET ACCEPTED — sender-side notification */}
      {outgoingPending.length > 0 && (
        <div
          className="bg-blue-50 border-b-2 border-blue-300 px-6 py-3 flex items-center justify-between cursor-pointer hover:bg-blue-100 transition-colors"
          onClick={() => { setShowOutgoingPending(true); fetchPendingTransfers(); }}
          data-testid="outgoing-pending-banner"
        >
          <div className="flex items-center gap-3">
            <ArrowRightLeft className="h-5 w-5 text-blue-600" />
            <div>
              <span className="font-bold text-blue-800">🚚 Outgoing Hindi Pa Tinatanggap: {outgoingPending.length}</span>
              <span className="text-sm text-blue-700 ml-2">May ipinadala kayong stock na hindi pa ina-accept ng {[...new Set(outgoingPending.map(t => t.destination_branch))].join(", ")}. I-click para makita at ma-follow up.</span>
            </div>
          </div>
          <span className="text-xs font-semibold bg-blue-500 text-white px-2 py-1 rounded-full">IN TRANSIT</span>
        </div>
      )}

      {/* Stats Bar */}
      <div className="bg-white border-b px-6 py-3">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Total Items:</span>
            <span className="font-bold text-gray-900">{stats.total}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Total Qty:</span>
            <span className="font-bold text-gray-900">{stats.totalQty.toLocaleString()}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-green-500"></span>
            <span className="text-sm text-gray-600">{stats.ok} OK</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-orange-500"></span>
            <span className="text-sm text-gray-600">{stats.low} Low</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-red-500"></span>
            <span className="text-sm text-gray-600">{stats.out} Out</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="px-6 py-4 flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search item name or code..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="ok">OK</SelectItem>
            <SelectItem value="low">Low / Critical</SelectItem>
            <SelectItem value="out">Out of Stock</SelectItem>
          </SelectContent>
        </Select>
        <Select value={locationFilter} onValueChange={setLocationFilter}>
          <SelectTrigger className="w-[200px]">
            <MapPin className="h-4 w-4 mr-2 text-gray-400" />
            <SelectValue placeholder="Location" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              <div className="flex items-center justify-between w-full">
                <span>All Locations</span>
                <span className="ml-2 text-xs text-gray-400">({inventory.length} items)</span>
              </div>
            </SelectItem>
            {locationStats.map(({ location, items, qty }) => (
              <SelectItem key={location} value={location}>
                <div className="flex items-center justify-between w-full">
                  <span>{location}</span>
                  <span className={`ml-2 text-xs ${items > 0 ? 'text-green-600 font-medium' : 'text-gray-400'}`}>
                    {items > 0 ? `${items} items (${qty})` : '0'}
                  </span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={fetchInventory} className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
        
        {/* Admin Tool: Fix Fractional Quantities */}
        {isAdmin() && (
          <Button 
            variant="outline" 
            onClick={fixFractionalQuantities} 
            className="gap-2 text-amber-600 border-amber-300 hover:bg-amber-50"
            title="Fix fractional quantities in non-Display Area locations"
          >
            <Settings className="h-4 w-4" />
            Fix Fractions
          </Button>
        )}
        
        {/* Admin Tool: REVERSE Auto-Reconcile Damage */}
        {isAdmin() && (
          <Button 
            variant="outline" 
            onClick={reverseAutoReconcile}
            disabled={isReversing}
            className="gap-2 text-red-600 border-red-300 hover:bg-red-50"
            title="Ibalik ang dating stock counts na nasira ng Auto-Reconcile"
          >
            <Undo2 className="h-4 w-4" />
            {isReversing ? "Reversing..." : "Reverse Auto-Reconcile"}
          </Button>
        )}
        
        {/* View Mode Toggle */}
        <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-lg ml-auto">
          <Button
            size="sm"
            variant={viewMode === 'card' ? 'default' : 'ghost'}
            onClick={() => setViewMode('card')}
            className={`h-8 px-3 ${viewMode === 'card' ? 'bg-indigo-600' : ''}`}
          >
            <LayoutGrid className="h-4 w-4 mr-1" />
            Cards
          </Button>
          <Button
            size="sm"
            variant={viewMode === 'list' ? 'default' : 'ghost'}
            onClick={() => setViewMode('list')}
            className={`h-8 px-3 ${viewMode === 'list' ? 'bg-indigo-600' : ''}`}
          >
            <List className="h-4 w-4 mr-1" />
            List
          </Button>
        </div>
        
        {/* Units/Boxes Toggle - NEW! */}
        <div className="flex items-center gap-1 p-1 bg-amber-50 border border-amber-200 rounded-lg">
          <Button
            size="sm"
            variant={unitMode === 'units' ? 'default' : 'ghost'}
            onClick={() => setUnitMode('units')}
            className={`h-8 px-3 ${unitMode === 'units' ? 'bg-amber-500 hover:bg-amber-600' : 'text-amber-700 hover:bg-amber-100'}`}
          >
            Units
          </Button>
          <Button
            size="sm"
            variant={unitMode === 'boxes' ? 'default' : 'ghost'}
            onClick={() => setUnitMode('boxes')}
            className={`h-8 px-3 ${unitMode === 'boxes' ? 'bg-amber-500 hover:bg-amber-600' : 'text-amber-700 hover:bg-amber-100'}`}
          >
            <Package className="h-4 w-4 mr-1" />
            Boxes
          </Button>
        </div>
      </div>

      {/* Inventory List */}
      <div className="px-6 pb-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin h-8 w-8 border-4 border-indigo-500 border-t-transparent rounded-full"></div>
          </div>
        ) : filteredInventory.length === 0 ? (
          <div className="text-center py-20 text-gray-500">
            {searchQuery || statusFilter !== "all" || locationFilter !== "all" ? "No items match your filters" : "No inventory items. Click 'Receive Stock' to add."}
          </div>
        ) : viewMode === 'list' ? (
          /* LIST VIEW - Dynamic columns for all locations */
          <div className="bg-white rounded-lg border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase sticky left-0 bg-gray-50">Item</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Total Qty</th>
                    {/* Dynamic location columns */}
                    {locationStats.map(({ location, items }) => (
                      <th key={location} className="px-3 py-3 text-center text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">
                        <div className="flex flex-col items-center">
                          <span>{location}</span>
                          <span className="text-[10px] text-gray-400 font-normal">({items} items)</span>
                        </div>
                      </th>
                    ))}
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredInventory.map((item, idx) => {
                    const status = getStockStatus(item.total_qty);
                    const StatusIcon = status.icon;
                    
                    return (
                      <tr key={item.item_name || `row-${idx}`} className="hover:bg-gray-50 group">
                        <td className="px-4 py-3 sticky left-0 bg-white group-hover:bg-gray-50">
                          <div className="flex items-center gap-3">
                            <div className={`w-2 h-10 rounded-full ${status.color.includes('red') ? 'bg-red-500' : status.color.includes('orange') ? 'bg-orange-500' : 'bg-green-500'}`} />
                            <div>
                              <button 
                                onClick={() => {
                                  setShowItemDetail(showItemDetail === `list-${idx}` ? null : `list-${idx}`);
                                  fetchItemHistory(item.item_name);
                                }}
                                className="font-medium text-gray-900 hover:text-indigo-600 hover:underline text-left"
                              >
                                {item.item_name}
                              </button>
                              <div className="text-xs text-gray-500">{item.item_code || 'No code'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {(() => {
                            const qty = formatQuantity(item.total_qty, item.item_name);
                            return (
                              <span className="font-bold text-lg text-gray-900">
                                {qty.value}
                                {unitMode === 'boxes' && qty.hasLoose && (
                                  <span className="text-sm text-amber-500">+{qty.loose}</span>
                                )}
                              </span>
                            );
                          })()}
                        </td>
                        {/* Dynamic location qty columns */}
                        {locationStats.map(({ location }) => {
                          const rawQty = item.locations?.[location] || 0;
                          const qty = formatQuantity(rawQty, item.item_name);
                          return (
                            <td key={location} className="px-3 py-3 text-center">
                              <button
                                onClick={() => rawQty > 0 && openEditLocation(item, location)}
                                disabled={rawQty === 0}
                                className={`font-medium ${rawQty > 0 ? 'text-indigo-600 hover:text-indigo-800 hover:underline cursor-pointer' : 'text-gray-300'}`}
                                title={rawQty > 0 ? `Click to move stock from ${location}` : 'No stock here'}
                              >
                                {rawQty === 0 ? '-' : (
                                  <>
                                    {qty.value}
                                    {unitMode === 'boxes' && qty.hasLoose && (
                                      <span className="text-xs text-amber-500">+{qty.loose}</span>
                                    )}
                                  </>
                                )}
                              </button>
                            </td>
                          );
                        })}
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
                            <StatusIcon className="h-3 w-3" />
                            {status.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            {Object.entries(ACTIONS).map(([key, action]) => {
                              if (!canEdit && key !== 'PULL_OUT') return null;
                              const ActionIcon = action.icon;
                              return (
                                <Button
                                  key={key}
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => openAction(key, item)}
                                  className={`h-8 w-8 p-0 ${action.color.replace('bg-', 'text-').replace('hover:bg-', 'hover:text-')}`}
                                  title={action.label}
                                >
                                  <ActionIcon className="h-4 w-4" />
                                </Button>
                              );
                            })}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setShowItemDetail(showItemDetail === `list-${idx}` ? null : `list-${idx}`);
                                fetchItemHistory(item.item_name);
                              }}
                              className="h-8 w-8 p-0 text-purple-600 hover:text-purple-700"
                              title="View History"
                            >
                            <History className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>
        ) : (
          /* CARD VIEW */
          <div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
            onClick={(e) => {
              // Close item detail popover when clicking outside
              if (!e.target.closest('[data-item-detail]')) {
                setShowItemDetail(null);
              }
            }}
          >
            {filteredInventory.map((item, idx) => {
              const status = getStockStatus(item.total_qty);
              const StatusIcon = status.icon;
              
              return (
                <Card
                  key={item.item_name || `card-${idx}`}
                  data-testid={`item-card-${idx}`}
                  className={`hover:shadow-lg transition-shadow border-2 ${status.color.includes('red') ? 'border-red-200' : status.color.includes('orange') ? 'border-orange-200' : 'border-gray-200'}`}
                >
                  <CardHeader className="pb-2 relative">
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex-1 min-w-0">
                        {/* Item Name - 2 lines max, clickable for full name */}
                        <button
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            const newIdx = showItemDetail === idx ? null : idx;
                            setShowItemDetail(newIdx); 
                            if (newIdx !== null) {
                              fetchItemHistory(item.item_name);
                            }
                          }}
                          className="text-left w-full group"
                          title={item.item_name}
                          data-item-detail="true"
                          data-testid={`item-name-${idx}`}
                        >
                          <CardTitle className="text-sm font-semibold leading-tight line-clamp-2 group-hover:text-indigo-600 group-hover:underline transition-colors cursor-pointer">
                            {item.item_name}
                          </CardTitle>
                        </button>
                        {/* Full name popover with HISTORY */}
                        {showItemDetail === idx && (
                          <div 
                            data-item-detail="true"
                            className="absolute z-50 mt-1 p-3 bg-white border-2 border-indigo-200 rounded-lg shadow-xl w-80 animate-in fade-in zoom-in-95 duration-150"
                          >
                            <div className="flex justify-between items-start gap-2">
                              <div className="font-semibold text-gray-900 break-words text-sm">{item.item_name}</div>
                              <button 
                                onClick={(e) => { e.stopPropagation(); setShowItemDetail(null); }}
                                className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                              >
                                <XCircle className="h-4 w-4" />
                              </button>
                            </div>
                            {/* Item Info */}
                            <div className="mt-2 text-xs text-gray-500 space-y-1 pb-2 border-b">
                              <div><span className="font-medium">Code:</span> {item.item_code || "N/A"}</div>
                              <div><span className="font-medium">Total Stock:</span> <span className="text-indigo-600 font-bold">{item.total_qty}</span></div>
                              {item.locations && Object.entries(item.locations).map(([loc, qty]) => {
                                const isTracking = isTrackingLocation(loc);
                                return (
                                  <div key={loc} className={`flex justify-between ${isTracking ? "opacity-70" : ""}`}>
                                    <span className={`${isTracking ? "text-amber-600" : "text-gray-600"}`}>
                                      {loc}{isTracking && " (audit)"}
                                    </span>
                                    <span className={`font-medium ${isTracking ? "text-amber-700" : ""}`}>
                                      {isTracking && <span className="text-[10px] mr-1">↩</span>}{qty}
                                    </span>
                                  </div>
                                );
                              })}
                              {/* Cargo Numbers for FIFO tracking */}
                              {item.rows && item.rows.some(r => r.cargo_number || r.date_received) && (
                                <div className="pt-1 mt-1 border-t border-gray-200">
                                  <span className="font-medium text-amber-600">📦 FIFO Tracking:</span>
                                  <div className="ml-2 space-y-0.5 mt-1">
                                    {item.rows.filter(r => r.stocks_qty > 0).sort((a, b) => {
                                      // Sort by date_received for FIFO (oldest first)
                                      const dateA = a.date_received || '9999';
                                      const dateB = b.date_received || '9999';
                                      return dateA.localeCompare(dateB);
                                    }).map((row, rowIdx) => (
                                      <div key={row.id || row.row_id || `${row.location || 'loc'}-${row.date_received || 'd'}-${rowIdx}`} className="flex justify-between text-xs bg-amber-50 rounded px-1.5 py-0.5">
                                        <div className="flex items-center gap-2">
                                          {row.cargo_number && (
                                            <span className="text-amber-700 font-mono text-[10px] bg-amber-100 px-1 rounded">{row.cargo_number}</span>
                                          )}
                                          <span className="text-gray-500 text-[10px]">
                                            {row.date_received || 'No date'} @ {row.location || 'Unknown'}
                                          </span>
                                        </div>
                                        <span className="text-amber-800 font-medium">{row.stocks_qty || row.quantity || 0}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                            {/* Activity History */}
                            <div className="mt-2">
                              <div className="flex items-center gap-1 text-xs font-semibold text-gray-700 mb-2">
                                <History className="h-3.5 w-3.5" />
                                Recent Activity
                              </div>
                              {loadingHistory && !itemHistory[item.item_name] ? (
                                <div className="text-xs text-gray-400 text-center py-2">Loading...</div>
                              ) : itemHistory[item.item_name]?.length > 0 ? (
                                <>
                                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                                  {itemHistory[item.item_name].slice(0, 8).map((log, logIdx) => (
                                    <div key={logIdx} className="flex items-start gap-2 text-xs bg-gray-50 rounded p-1.5">
                                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                        log.action_type === 'RECEIVE' ? 'bg-green-100 text-green-700' :
                                        log.action_type === 'PULL_OUT' ? 'bg-red-100 text-red-700' :
                                        log.action_type === 'RECOUNT' ? 'bg-blue-100 text-blue-700' :
                                        log.action_type === 'INTERNAL_MOVE' ? 'bg-indigo-100 text-indigo-700' :
                                        log.action_type?.includes('TRANSFER') ? 'bg-purple-100 text-purple-700' :
                                        log.action_type?.includes('SALE') ? 'bg-orange-100 text-orange-700' :
                                        log.action_type?.includes('WARRANTY') ? 'bg-amber-100 text-amber-700' :
                                        'bg-gray-100 text-gray-700'
                                      }`}>
                                        {log.action_type?.replace(/_/g, ' ') || 'ACTION'}
                                      </span>
                                      <div className="flex-1 min-w-0">
                                        <div className="text-gray-600">
                                          {/* Display quantity with proper sign and color */}
                                          {(() => {
                                            // RECOUNT carries a SIGNED delta — respect its sign
                                            if (log.action_type === 'RECOUNT') {
                                              const delta = (log.quantity ?? log.difference ?? 0);
                                              return delta < 0
                                                ? <span className="text-red-600 font-medium">-{Math.abs(delta)}</span>
                                                : <span className="text-green-600 font-medium">+{Math.abs(delta)}</span>;
                                            }
                                            // INTERNAL_MOVE is net-zero (location change only)
                                            if (log.action_type === 'INTERNAL_MOVE') {
                                              return <span className="text-indigo-600 font-medium">{Math.abs(log.quantity || 0)}</span>;
                                            }
                                            const isSaleOrDeduction = log.action_type?.includes('SALE') || 
                                                                      log.action_type === 'PULL_OUT' || 
                                                                      log.action_type?.includes('TRANSFER_OUT') ||
                                                                      log.action_type === 'TRANSFER' ||
                                                                      log.action_type?.includes('WARRANTY') ||
                                                                      log.stock_change < 0;
                                            const absQty = Math.abs(log.quantity || log.stock_change || 0);
                                            
                                            if (isSaleOrDeduction && log.action_type !== 'TRANSFER_IN') {
                                              return <span className="text-red-600 font-medium">-{absQty}</span>;
                                            } else {
                                              return <span className="text-green-600 font-medium">+{absQty}</span>;
                                            }
                                          })()} units
                                          {log.location && <span className="text-gray-400"> @ {log.location}</span>}
                                          {/* Show internal move route */}
                                          {log.action_type === 'INTERNAL_MOVE' && log.from_location && log.to_location && (
                                            <span className="text-indigo-500 ml-1">{log.from_location} → {log.to_location}</span>
                                          )}
                                          {/* Show transfer details */}
                                          {log.action_type?.includes('TRANSFER') && (log.from_branch || log.to_branch) && (
                                            <span className="text-purple-500 ml-1">
                                              {log.from_branch && log.to_branch ? `${log.from_branch} → ${log.to_branch}` : 
                                               log.to_branch ? `→ ${log.to_branch}` : 
                                               log.from_branch ? `← ${log.from_branch}` : ''}
                                            </span>
                                          )}
                                        </div>
                                        <div className="text-[10px] text-gray-400">
                                          {log.encoder && <span>{log.encoder} • </span>}
                                          {log.timestamp ? new Date(log.timestamp).toLocaleDateString('en-PH', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                {/* View All History Button */}
                                {itemHistory[item.item_name]?.length >= 8 && (
                                  <button
                                    onClick={() => {
                                      setFullHistoryItem(item);
                                      fetchItemHistory(item.item_name, true);
                                      setShowFullHistory(true);
                                    }}
                                    className="w-full mt-2 py-1.5 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors flex items-center justify-center gap-1"
                                  >
                                    <History className="h-3 w-3" />
                                    View All History
                                  </button>
                                )}
                              </>
                              ) : (
                                <div className="text-xs text-gray-400 text-center py-2">No activity recorded</div>
                              )}
                            </div>
                          </div>
                        )}
                        <p className="text-xs text-gray-500 mt-1">{item.item_code || "No code"}</p>
                      </div>
                      <span className={`flex-shrink-0 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${status.color}`}>
                        <StatusIcon className="h-3 w-3" />
                        {status.label}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Stock Quantity - Shows based on unitMode */}
                    <div className="mb-3">
                      {(() => {
                        const qty = formatQuantity(item.total_qty, item.item_name);
                        const boxInfo = getBoxInfo(item.item_name);
                        return (
                          <>
                            <div className="text-3xl font-bold text-gray-900" data-testid={`item-total-qty-${idx}`}>
                              {qty.value}
                              {unitMode === 'boxes' && qty.hasLoose && (
                                <span className="text-lg text-amber-500 ml-1">+{qty.loose}</span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 flex items-center gap-1">
                              {unitMode === 'boxes' ? (
                                <>
                                  <span>{qty.label}</span>
                                  {qty.setsPerBox ? (
                                    <button
                                      onClick={() => {
                                        setEditingBoxItem(item);
                                        setNewSetsPerBox(qty.setsPerBox);
                                      }}
                                      className="text-amber-600 hover:text-amber-800 hover:underline"
                                      title="Click to edit units per box"
                                    >
                                      ({qty.setsPerBox}/box)
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => {
                                        setEditingBoxItem(item);
                                        setNewSetsPerBox(40);
                                      }}
                                      className="text-red-500 hover:text-red-700 underline"
                                      title="Set units per box"
                                    >
                                      (set box size)
                                    </button>
                                  )}
                                </>
                              ) : (
                                'total in stock'
                              )}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                    
                    {/* Location Breakdown - CLICKABLE TO EDIT */}
                    <div className="mb-4 space-y-1">
                      {Object.entries(item.locations || {}).filter(([_, qty]) => qty > 0).map(([loc, qty], locIdx) => {
                        const locQty = formatQuantity(qty, item.item_name);
                        const isTracking = isTrackingLocation(loc);
                        return (
                          <div
                            key={loc}
                            className={`flex items-center justify-between text-sm ${isTracking ? "opacity-60 bg-amber-50/50 rounded px-1" : ""}`}
                            data-testid={`item-location-row-${idx}-${locIdx}`}
                          >
                            <button
                              onClick={() => !isTracking && openEditLocation(item, loc)}
                              disabled={isTracking}
                              className={`flex items-center gap-1.5 px-2 py-1 rounded transition-colors group ${
                                isTracking ? "cursor-not-allowed" : "hover:bg-gray-100 cursor-pointer"
                              }`}
                              title={isTracking ? "Audit-only tracking record (not real stock)" : "Click to change location"}
                              data-testid={`edit-item-location-${idx}-${locIdx}`}
                            >
                              <MapPin className={`h-3.5 w-3.5 ${
                                isTracking ? "text-amber-500" :
                                loc === 'Display Area' ? 'text-green-500' :
                                loc === 'Stock Room' ? 'text-blue-500' : 'text-purple-500'
                              }`} />
                              <span className={`${isTracking ? "text-amber-700 italic" : "text-gray-600 group-hover:text-indigo-600"}`}>
                                {loc}{isTracking && " (audit)"}
                              </span>
                              {!isTracking && (
                                <svg className="h-3 w-3 opacity-0 group-hover:opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                </svg>
                              )}
                            </button>
                            <span
                              className={`font-semibold ${
                                isTracking ? "text-amber-600" :
                                loc === 'Display Area' ? 'text-green-600' :
                                loc === 'Stock Room' ? 'text-blue-600' : 'text-purple-600'
                              }`}
                              data-testid={`item-location-qty-${idx}-${locIdx}`}
                            >
                              {isTracking && <span className="text-[10px] mr-1">↩</span>}
                              {locQty.value}
                              {unitMode === 'boxes' && locQty.hasLoose && (
                                <span className="text-xs text-amber-500 ml-0.5">+{locQty.loose}</span>
                              )}
                            </span>
                          </div>
                        );
                      })}
                      {(!item.locations || Object.keys(item.locations).length === 0) && (
                        <div className="text-xs text-gray-400">No location data</div>
                      )}
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="grid grid-cols-2 gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => openAction("RECEIVE", item)}
                        className="text-green-600 border-green-200 hover:bg-green-50"
                        data-testid={`receive-btn-${idx}`}
                        disabled={!canEdit}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Receive
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => openAction("PULL_OUT", item)}
                        disabled={item.total_qty <= 0 || !canEdit}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        data-testid={`pullout-btn-${idx}`}
                      >
                        <Minus className="h-3 w-3 mr-1" />
                        Pull Out
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => openAction("TRANSFER", item)}
                        disabled={item.total_qty <= 0 || !canEdit}
                        className="text-orange-600 border-orange-200 hover:bg-orange-50"
                        data-testid={`transfer-btn-${idx}`}
                      >
                        <ArrowRightLeft className="h-3 w-3 mr-1" />
                        Transfer
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => openAction("RECOUNT", item)}
                        className="text-blue-600 border-blue-200 hover:bg-blue-50"
                        data-testid={`recount-btn-${idx}`}
                        disabled={!canEdit}
                      >
                        <RefreshCw className="h-3 w-3 mr-1" />
                        Recount
                      </Button>
                    </div>
                    
                    {/* Delete Button - Admin only for safety */}
                    {isAdmin() && (
                      <div className="mt-2 pt-2 border-t border-gray-100">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => setItemToDelete(item)}
                          className="w-full text-gray-500 border-gray-200 hover:bg-red-50 hover:text-red-600 hover:border-red-200"
                          data-testid={`delete-btn-${idx}`}
                          disabled={!canEdit}
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Delete Record
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Action Modal */}
      <Dialog open={!!activeAction} onOpenChange={(open) => !open && closeModal()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {activeAction && (
                <>
                  {(() => {
                    const ActionIcon = ACTIONS[activeAction]?.icon;
                    return ActionIcon ? <ActionIcon className="h-5 w-5" /> : null;
                  })()}
                  {ACTIONS[activeAction]?.label}
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {activeAction ? ACTIONS[activeAction]?.description : ""}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* For RECEIVE without selected item - show item search */}
            {activeAction === "RECEIVE" && !selectedItem && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Select Item *</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search item to receive..."
                    value={newItemSearch}
                    onChange={(e) => {
                      setNewItemSearch(e.target.value);
                      setNewItem(null);
                    }}
                    className="pl-10"
                    autoFocus
                  />
                </div>
                
                {newItemSearch && !newItem && filteredNewItems.length > 0 && (
                  <div className="border rounded-lg max-h-40 overflow-y-auto">
                    {filteredNewItems.map(item => (
                      <button
                        key={item.id}
                        onClick={() => {
                          setNewItem(item);
                          setNewItemSearch(item.item_name);
                          setFilteredNewItems([]);
                        }}
                        className="w-full px-3 py-2 text-left hover:bg-gray-50 border-b last:border-b-0"
                      >
                        <div className="font-medium text-sm">{item.item_name}</div>
                        <div className="text-xs text-gray-500">{item.category}</div>
                      </button>
                    ))}
                  </div>
                )}
                
                {newItem && (
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="font-semibold text-green-900">{newItem.item_name}</div>
                    <div className="text-sm text-green-700">{newItem.category}</div>
                  </div>
                )}
              </div>
            )}
            
            {/* Show selected item info */}
            {selectedItem && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="font-semibold text-gray-900">{selectedItem.item_name}</div>
                <div className="flex justify-between items-center mt-1">
                  <span className="text-sm text-gray-600">
                    Total Stock: <span className="font-bold text-lg text-green-600">{selectedItem.total_qty}</span> units
                  </span>
                </div>
                {/* Source Location Selector for TRANSFER */}
                {activeAction === "TRANSFER" && selectedItem && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <label className="text-sm font-bold text-gray-700 block mb-2">
                      📍 Saan babawasan ng stock? *
                    </label>
                    <div className="space-y-2">
                      {(() => {
                        // Get rows with stock grouped by location
                        const rowsWithStock = (selectedItem.rows || [])
                          .filter(r => (r.stocks_qty || r.quantity || 0) > 0);
                        
                        // Group by location
                        const locationStocks = {};
                        rowsWithStock.forEach(row => {
                          const loc = row.location || 'Unknown';
                          const qty = row.stocks_qty || row.quantity || 0;
                          if (!locationStocks[loc]) {
                            locationStocks[loc] = { qty: 0, rows: [] };
                          }
                          locationStocks[loc].qty += qty;
                          locationStocks[loc].rows.push(row);
                        });
                        
                        const locations = Object.entries(locationStocks);
                        
                        if (locations.length === 0) {
                          return <div className="text-sm text-gray-500">Walang available na stock</div>;
                        }
                        
                        return (
                          <div className="grid gap-2">
                            {locations.map(([loc, data]) => (
                              <button
                                key={loc}
                                type="button"
                                onClick={() => setSourceLocation(loc)}
                                className={`p-3 rounded-lg border-2 text-left transition-all ${
                                  sourceLocation === loc 
                                    ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' 
                                    : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                      sourceLocation === loc ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                                    }`}>
                                      {sourceLocation === loc && (
                                        <Check className="h-3 w-3 text-white" />
                                      )}
                                    </div>
                                    <span className={`font-medium ${sourceLocation === loc ? 'text-blue-700' : 'text-gray-700'}`}>
                                      {loc}
                                    </span>
                                  </div>
                                  <span className={`font-bold text-lg ${
                                    sourceLocation === loc ? 'text-blue-600' : 'text-gray-600'
                                  }`}>
                                    {data.qty} units
                                  </span>
                                </div>
                              </button>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                    {sourceLocation && (
                      <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200 text-sm">
                        <span className="text-blue-700">✓ Babawasan ang stock sa: </span>
                        <span className="font-bold text-blue-800">{sourceLocation}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Quantity input for RECEIVE, PULL_OUT, TRANSFER */}
            {["RECEIVE", "PULL_OUT", "TRANSFER"].includes(activeAction) && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Quantity ({unitMode === 'boxes' && getBoxInfo(selectedItem?.item_name) ? 'boxes' : 'units'}) *
                </label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="0.5"
                    step="0.5"
                    max={activeAction !== "RECEIVE" && selectedItem ? 
                      (unitMode === 'boxes' && getBoxInfo(selectedItem?.item_name) ? 
                        Math.floor(selectedItem.total_qty / getBoxInfo(selectedItem?.item_name).setsPerBox) : 
                        selectedItem.total_qty) : undefined}
                    value={actionQty}
                    onChange={(e) => setActionQty(parseFloat(e.target.value) || 0.5)}
                    className="text-center text-lg font-bold"
                    data-testid="action-qty-input"
                  />
                  {unitMode === 'boxes' && getBoxInfo(selectedItem?.item_name) && (
                    <div className="text-sm text-amber-600 bg-amber-50 px-2 py-1 rounded whitespace-nowrap">
                      = {actionQty * getBoxInfo(selectedItem?.item_name).setsPerBox} units
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-1">Pwede 0.5 para sa kalahating box o isang piraso</p>
                {/* Warning if exceeds stock */}
                {activeAction !== "RECEIVE" && selectedItem && (() => {
                  const boxInfo = getBoxInfo(selectedItem?.item_name);
                  const maxQty = unitMode === 'boxes' && boxInfo ? 
                    Math.floor(selectedItem.total_qty / boxInfo.setsPerBox) : selectedItem.total_qty;
                  const actualQty = unitMode === 'boxes' && boxInfo ? actionQty * boxInfo.setsPerBox : actionQty;
                  if (actualQty > selectedItem.total_qty) {
                    return (
                      <div className="flex items-center gap-2 text-red-600 text-sm">
                        <AlertTriangle className="h-4 w-4" />
                        Exceeds available! Max: {maxQty} {unitMode === 'boxes' && boxInfo ? 'boxes' : 'units'}
                      </div>
                    );
                  }
                  return null;
                })()}
                {/* Show box info hint */}
                {unitMode === 'boxes' && getBoxInfo(selectedItem?.item_name) && (
                  <div className="text-xs text-gray-500">
                    📦 1 box = {getBoxInfo(selectedItem?.item_name).setsPerBox} units
                  </div>
                )}
                {unitMode === 'boxes' && !getBoxInfo(selectedItem?.item_name) && (
                  <div className="text-xs text-amber-600">
                    ⚠️ No box size set for this item. Counting in units.
                  </div>
                )}
              </div>
            )}
            
            {/* Location selector for RECEIVE */}
            {activeAction === "RECEIVE" && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
                  <MapPin className="h-4 w-4 text-indigo-500" />
                  Where will the item go? *
                </label>
                <Select value={actionLocation} onValueChange={setActionLocation}>
                  <SelectTrigger data-testid="receive-location-select">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    {branchLocations.map(loc => (
                      <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                    ))}
                    <SelectItem value="custom">+ Add New Location...</SelectItem>
                  </SelectContent>
                </Select>
                {actionLocation === "custom" && (
                  <Input
                    placeholder="Enter new location name..."
                    value={customLocation}
                    onChange={(e) => setCustomLocation(e.target.value)}
                    className="mt-2"
                    data-testid="custom-location-input"
                  />
                )}
              </div>
            )}
            
            {/* Date picker for RECEIVE */}
            {activeAction === "RECEIVE" && (
              <div className="space-y-3">
                {/* ANTI-THEFT: Mandatory source/reason for receive */}
                <div className="space-y-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <label className="text-sm font-medium text-amber-900 flex items-center gap-1">
                    <Lock className="h-4 w-4 text-amber-700" />
                    Source / Reason *
                    <span className="text-xs text-amber-700 font-normal">(naka-log sa Stock Audit)</span>
                  </label>
                  <Select value={receiveSource} onValueChange={setReceiveSource}>
                    <SelectTrigger className="h-10 bg-white" data-testid="receive-source-select">
                      <SelectValue placeholder="Saan galing yung stock?" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="New Purchase">📦 New Purchase / Cargo (kailangan ng Cargo #)</SelectItem>
                      <SelectItem value="Customer Return">↩ Customer Return (working item)</SelectItem>
                      <SelectItem value="Transfer In">🔄 Transfer In (from another branch)</SelectItem>
                      <SelectItem value="Warranty Reserve Restock">🛡 Warranty Reserve Restock</SelectItem>
                      <SelectItem value="Found Stock">🔍 Found / Recovered Stock (recount adjustment)</SelectItem>
                      <SelectItem value="Other">📝 Other (need note)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-amber-700">
                    {receiveSource === "New Purchase" && "📌 Cargo # required below."}
                    {receiveSource === "Other" && "📌 Notes required (min 5 chars)."}
                    {receiveSource === "Found Stock" && "⚠️ Use this only after physical recount confirms missing stock found."}
                    {!receiveSource && "Hindi ma-receive nang walang source. Anti-fraud measure."}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
                      <Calendar className="h-4 w-4 text-green-500" />
                      Date Received
                    </label>
                    <Input
                      type="date"
                      value={receiveDate}
                      onChange={(e) => setReceiveDate(e.target.value)}
                      className="w-full"
                      data-testid="receive-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
                      <Package className="h-4 w-4 text-blue-500" />
                      Cargo # {receiveSource === "New Purchase" ? "*" : "(FIFO)"}
                    </label>
                    <Input
                      type="text"
                      value={cargoNumber}
                      onChange={(e) => setCargoNumber(e.target.value)}
                      placeholder="e.g., CARGO-001"
                      className="w-full"
                      data-testid="cargo-number-input"
                    />
                  </div>
                </div>
              </div>
            )}
            
            {/* PULL OUT - Source and Destination locations */}
            {activeAction === "PULL_OUT" && selectedItem && (
              <div className="space-y-4">
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800 font-medium">
                    📦 Internal Movement: Move stock within {selectedBranch} branch
                  </p>
                </div>
                
                {/* Pull Out Date */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Date of Pull Out</label>
                  <Input
                    type="date"
                    value={pullOutDate}
                    onChange={(e) => setPullOutDate(e.target.value)}
                    className="w-full"
                    data-testid="pullout-date-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">From (Source)</label>
                    <Select value={sourceLocation} onValueChange={setSourceLocation}>
                      <SelectTrigger data-testid="source-location-select">
                        <SelectValue placeholder="Current location" />
                      </SelectTrigger>
                      <SelectContent>
                        {branchLocations.map(loc => (
                          <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">To (Destination)</label>
                    <Select value={destinationLocation} onValueChange={setDestinationLocation}>
                      <SelectTrigger data-testid="destination-location-select">
                        <SelectValue placeholder="New location" />
                      </SelectTrigger>
                      <SelectContent>
                        {branchLocations.map(loc => (
                          <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                        ))}
                        <SelectItem value="custom">+ Add New Location...</SelectItem>
                      </SelectContent>
                    </Select>
                    {destinationLocation === "custom" && (
                      <Input
                        placeholder="Enter new location name..."
                        value={customLocation}
                        onChange={(e) => setCustomLocation(e.target.value)}
                        className="mt-2"
                      />
                    )}
                  </div>
                </div>
                {sourceLocation && destinationLocation && sourceLocation !== destinationLocation && destinationLocation !== "custom" && (
                  <div className="flex items-center justify-center gap-2 text-indigo-600 font-medium">
                    <span>{sourceLocation}</span>
                    <ArrowRightLeft className="h-4 w-4" />
                    <span>{destinationLocation}</span>
                  </div>
                )}
              </div>
            )}
            
            {/* Destination branch for TRANSFER */}
            {activeAction === "TRANSFER" && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Transfer to Branch *</label>
                <Select value={transferBranch} onValueChange={setTransferBranch}>
                  <SelectTrigger data-testid="transfer-branch-select">
                    <SelectValue placeholder="Select destination" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(BRANCHES)
                      .filter(([code]) => code !== selectedBranch)
                      .map(([code, name]) => (
                        <SelectItem key={code} value={code}>{code} - {name}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  💡 Receiving branch will choose where to place the item
                </p>
              </div>
            )}

            {/* ANTI-THEFT: Mandatory reason for TRANSFER */}
            {activeAction === "TRANSFER" && (
              <div className="space-y-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <label className="text-sm font-medium text-amber-900 flex items-center gap-1">
                  <Lock className="h-4 w-4 text-amber-700" />
                  Reason for Transfer *
                  <span className="text-xs text-amber-700 font-normal">(naka-log sa Stock Audit)</span>
                </label>
                <Select value={transferReason} onValueChange={setTransferReason}>
                  <SelectTrigger className="h-10 bg-white" data-testid="transfer-reason-select">
                    <SelectValue placeholder="Bakit ito ililipat?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Branch Request">📥 Branch Request (hiniling ng destination)</SelectItem>
                    <SelectItem value="Low Stock Replenishment">📊 Low Stock Replenishment</SelectItem>
                    <SelectItem value="Display Sample">🪟 Display Sample</SelectItem>
                    <SelectItem value="Customer Order">🛍 Customer Order (specific customer)</SelectItem>
                    <SelectItem value="Reallocation">🔁 Reallocation (rebalance stocks)</SelectItem>
                    <SelectItem value="Other">📝 Other (need note)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-amber-700">
                  {transferReason === "Other" && "📌 Notes required (min 5 chars)."}
                  {!transferReason && "Hindi pwedeng i-transfer nang walang reason. Anti-fraud measure."}
                </p>
              </div>
            )}

            {/* ANTI-THEFT: Mandatory reason for PULL OUT (internal movement) */}
            {activeAction === "PULL_OUT" && (
              <div className="space-y-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <label className="text-sm font-medium text-amber-900 flex items-center gap-1">
                  <Lock className="h-4 w-4 text-amber-700" />
                  Reason for Pull Out *
                  <span className="text-xs text-amber-700 font-normal">(naka-log sa Stock Audit)</span>
                </label>
                <Select value={pullOutReason} onValueChange={setPullOutReason}>
                  <SelectTrigger className="h-10 bg-white" data-testid="pullout-reason-select">
                    <SelectValue placeholder="Bakit ililipat ang stock?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Restocking">📦 Restocking (Stock Room → Display Area)</SelectItem>
                    <SelectItem value="Display Replacement">🪟 Display Replacement (replace sold/damaged)</SelectItem>
                    <SelectItem value="Damaged Removal">⚠️ Damaged Removal (move to damaged area)</SelectItem>
                    <SelectItem value="Layout Change">📐 Layout Change / Reorganize</SelectItem>
                    <SelectItem value="Customer Reservation">🔒 Customer Reservation</SelectItem>
                    <SelectItem value="Inventory Adjustment">📊 Inventory Adjustment</SelectItem>
                    <SelectItem value="Other">📝 Other (need note)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-amber-700">
                  {pullOutReason === "Other" && "📌 Notes required (min 5 chars)."}
                  {pullOutReason === "Damaged Removal" && "⚠️ Damaged items should be tracked in Warranty - Defective if applicable."}
                  {!pullOutReason && "Hindi pwedeng mag-pull out nang walang reason. Anti-fraud measure."}
                </p>
              </div>
            )}
            
            {/* RECOUNT - Location-specific recount */}
            {activeAction === "RECOUNT" && selectedItem && (
              <>
                {/* Step 1: Select which location to recount */}
                <div className="space-y-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <label className="text-sm font-semibold text-blue-800 flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    Piliin ang Location na ire-recount
                  </label>
                  <Select 
                    value={recountSourceLocation} 
                    onValueChange={(val) => {
                      setRecountSourceLocation(val);
                      // Update the qty to match the selected location's current stock
                      const locationQty = selectedItem.locations?.[val] || 0;
                      setRecountNewQty(locationQty);
                      setRecountNewLocation(val); // Default destination to same location
                    }}
                  >
                    <SelectTrigger className="bg-white" data-testid="recount-source-location-select">
                      <SelectValue placeholder="Select location to recount" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedItem.locations && Object.entries(selectedItem.locations).map(([loc, qty]) => (
                        <SelectItem key={loc} value={loc}>
                          {loc} ({qty} units)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {recountSourceLocation && (
                    <p className="text-xs text-blue-600">
                      📍 Ire-recount lang ang stocks sa <strong>{recountSourceLocation}</strong> ({selectedItem.locations?.[recountSourceLocation] || 0} units)
                    </p>
                  )}
                </div>

                {/* Step 2: Enter new quantity for that location */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Bagong bilang para sa {recountSourceLocation || "location"} *</label>
                  <Input
                    type="number"
                    min="0"
                    step="0.5"
                    value={recountNewQty}
                    onChange={(e) => setRecountNewQty(parseFloat(e.target.value) || 0)}
                    className="text-center text-lg font-bold"
                    data-testid="recount-qty-input"
                  />
                  {/* Warning for fractional in non-Display Area (based on FINAL destination) */}
                  {!(getFinalRecountLocation() || recountSourceLocation)?.toLowerCase().startsWith('display') && recountNewQty !== Math.floor(recountNewQty) && (
                    <p className="text-xs text-red-500 font-medium">
                      ⚠️ Hindi pwede ang fractional (hal. 0.5) sa {getFinalRecountLocation() || recountSourceLocation}! Display Area lang ang may fractional — ire-reject ito ng system. Gumamit ng whole number.
                    </p>
                  )}
                  {recountSourceLocation?.toLowerCase().startsWith('display') && (
                    <p className="text-xs text-gray-400">Pwede mag-lagay ng 0.5 para sa isang piraso (Display Area only)</p>
                  )}
                  {!recountSourceLocation?.toLowerCase().startsWith('display') && (
                    <p className="text-xs text-amber-500">⚠️ {recountSourceLocation} - Whole numbers lang (walang 0.5)</p>
                  )}
                  {recountSourceLocation && (
                    <div className="text-sm text-center text-gray-500">
                      {recountSourceLocation}: {selectedItem.locations?.[recountSourceLocation] || 0} → {recountNewQty}
                      <span className={`ml-2 font-bold ${recountNewQty - (selectedItem.locations?.[recountSourceLocation] || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        ({recountNewQty - (selectedItem.locations?.[recountSourceLocation] || 0) >= 0 ? '+' : ''}{recountNewQty - (selectedItem.locations?.[recountSourceLocation] || 0)})
                      </span>
                    </div>
                  )}
                </div>
                
                {/* Step 3: Optional - Move to different location */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
                    <ArrowRightLeft className="h-4 w-4 text-indigo-500" />
                    Destination Location (kung lilipat)
                  </label>
                  <Select value={recountNewLocation} onValueChange={setRecountNewLocation}>
                    <SelectTrigger data-testid="recount-location-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {branchLocations.map(loc => (
                        <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                      ))}
                      <SelectItem value="custom">+ Add New Location...</SelectItem>
                    </SelectContent>
                  </Select>
                  {recountNewLocation === "custom" && (
                    <Input
                      placeholder="Enter new location name..."
                      value={customLocation}
                      onChange={(e) => setCustomLocation(e.target.value)}
                      className="mt-2"
                    />
                  )}
                  {recountNewLocation !== recountSourceLocation && recountNewLocation !== "custom" && (
                    <div className="text-sm text-indigo-600 flex items-center gap-1">
                      <ArrowRightLeft className="h-3 w-3" />
                      Moving from {recountSourceLocation} → {recountNewLocation}
                    </div>
                  )}
                </div>
                
                {/* Date of Recount */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
                    <Calendar className="h-4 w-4 text-blue-500" />
                    Date of Recount
                  </label>
                  <Input
                    type="date"
                    value={recountDate}
                    onChange={(e) => setRecountDate(e.target.value)}
                    className="w-full"
                    data-testid="recount-date-input"
                  />
                </div>
              </>
            )}
            
            {/* Notes */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Notes {activeAction === "RECOUNT" ? "(e.g. 'Physical count – Feb 4')" : "(optional)"}
              </label>
              <Input
                value={actionNotes}
                onChange={(e) => setActionNotes(e.target.value)}
                placeholder={activeAction === "RECOUNT" ? "Physical count – Feb 4" : "Add notes..."}
                data-testid="action-notes-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={closeModal} disabled={processing}>
              Cancel
            </Button>
            <Button 
              onClick={executeAction}
              disabled={processing || 
                (activeAction === "RECEIVE" && !selectedItem && !newItem) ||
                (activeAction === "RECEIVE" && !getFinalLocation()) ||
                (activeAction === "RECEIVE" && !receiveSource) ||
                (["PULL_OUT", "TRANSFER"].includes(activeAction) && selectedItem && actionQty > selectedItem.total_qty) ||
                (activeAction === "TRANSFER" && !transferBranch) ||
                (activeAction === "TRANSFER" && !sourceLocation) ||
                (activeAction === "TRANSFER" && !transferReason) ||
                (activeAction === "PULL_OUT" && !pullOutReason)
              }
              className={ACTIONS[activeAction]?.color}
              data-testid="execute-action-btn"
            >
              {processing ? "Processing..." : `Confirm ${ACTIONS[activeAction]?.label}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activity History Modal */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-blue-600" />
              Activity History - {selectedBranch}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {/* Lookback period filter */}
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <p className="text-sm text-gray-500">
                Click the trash icon to delete and reverse any action. Stock will be adjusted automatically.
              </p>
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-gray-600">Show last:</label>
                <Select 
                  value={String(activityLookbackDays)} 
                  onValueChange={(v) => { 
                    const newDays = parseInt(v);
                    setActivityLookbackDays(newDays);
                    // Auto-refetch with new range
                    setTimeout(() => fetchActivityLogs(), 100);
                  }}
                >
                  <SelectTrigger className="w-[130px] h-8 text-xs" data-testid="activity-lookback-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">Last 7 days</SelectItem>
                    <SelectItem value="30">Last 30 days</SelectItem>
                    <SelectItem value="60">Last 60 days</SelectItem>
                    <SelectItem value="90">Last 90 days</SelectItem>
                    <SelectItem value="180">Last 180 days</SelectItem>
                    <SelectItem value="365">Last 365 days</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={fetchActivityLogs}
                  className="h-8 px-2 text-xs"
                  data-testid="activity-refresh-btn"
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Refresh
                </Button>
              </div>
            </div>
            <div className="text-xs text-blue-600 mb-2">
              📊 Showing {activityLogs.length} activities mula sa nakaraang {activityLookbackDays} araw
            </div>
            <div className="max-h-[50vh] overflow-y-auto space-y-2">
              {loadingLogs ? (
                <div className="text-center py-4 text-gray-500">Loading...</div>
              ) : activityLogs.length === 0 ? (
                <div className="text-center py-4 text-gray-500">No activity yet</div>
              ) : (
                activityLogs.map(log => (
                  <div key={log.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 group">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                          log.action_type === 'RECEIVE' ? 'bg-green-100 text-green-700' :
                          log.action_type === 'PULL_OUT' ? 'bg-red-100 text-red-700' :
                          log.action_type?.includes('TRANSFER') ? 'bg-orange-100 text-orange-700' :
                          log.action_type === 'RECOUNT' ? 'bg-blue-100 text-blue-700' :
                          log.action_type === 'INTERNAL_MOVE' ? 'bg-indigo-100 text-indigo-700' :
                          log.action_type?.includes('DELETE') ? 'bg-gray-100 text-gray-500 line-through' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {log.action_type}
                        </span>
                        <span className="font-medium text-sm truncate" title={log.item_name}>{log.item_name}</span>
                        {/* Show proper sign based on action type (handles both old and new logs) */}
                        {(() => {
                          if (log.action_type === 'RECOUNT') {
                            const delta = (log.quantity ?? log.difference ?? 0);
                            return delta < 0
                              ? <span className="text-sm font-bold text-red-600">-{Math.abs(delta)}</span>
                              : <span className="text-sm font-bold text-green-600">+{Math.abs(delta)}</span>;
                          }
                          if (log.action_type === 'INTERNAL_MOVE') {
                            return <span className="text-sm font-bold text-indigo-600">{Math.abs(log.quantity || 0)}</span>;
                          }
                          const isSaleOrDeduction = log.action_type?.includes('SALE') || 
                                                    log.action_type === 'PULL_OUT' || 
                                                    log.action_type?.includes('TRANSFER_OUT');
                          const absQty = Math.abs(log.quantity || 0);
                          
                          if (isSaleOrDeduction) {
                            return <span className="text-sm font-bold text-red-600">-{absQty}</span>;
                          } else {
                            return <span className="text-sm font-bold text-green-600">+{absQty}</span>;
                          }
                        })()}
                        {log.location && (
                          <span className="text-xs bg-purple-50 text-purple-600 px-1.5 py-0.5 rounded">
                            📍 {log.location}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {log.timestamp?.slice(0, 16).replace('T', ' ')} • {log.encoder}
                        {log.destination_branch && ` → ${log.destination_branch}`}
                        {log.notes && <span className="ml-2 italic">&ldquo;{log.notes.slice(0, 50)}...&rdquo;</span>}
                      </div>
                    </div>
                    {!log.action_type?.includes('DELETE') && (
                      <button
                        onClick={() => confirmDeleteLog(log)}
                        className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-100 rounded-full border border-red-200 flex-shrink-0 ml-2"
                        title="Delete and reverse this action"
                        data-testid={`delete-log-${log.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHistory(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Log Confirmation Modal */}
      <Dialog open={!!logToDelete} onOpenChange={() => setLogToDelete(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete & Reverse Action
            </DialogTitle>
          </DialogHeader>
          {logToDelete && (
            <div className="py-4 space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="font-medium text-red-800 mb-2">You are about to delete:</p>
                <div className="space-y-1 text-sm">
                  <div><span className="text-gray-600">Action:</span> <span className="font-medium">{logToDelete.action_type}</span></div>
                  <div><span className="text-gray-600">Item:</span> <span className="font-medium">{logToDelete.item_name}</span></div>
                  <div><span className="text-gray-600">Quantity:</span> <span className="font-medium">{logToDelete.quantity}</span></div>
                  {logToDelete.location && <div><span className="text-gray-600">Location:</span> <span className="font-medium">{logToDelete.location}</span></div>}
                  <div><span className="text-gray-600">Date:</span> <span className="font-medium">{logToDelete.timestamp?.slice(0, 16).replace('T', ' ')}</span></div>
                  <div><span className="text-gray-600">By:</span> <span className="font-medium">{logToDelete.encoder}</span></div>
                </div>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-yellow-800 text-sm">
                  <strong>Impact:</strong> {
                    logToDelete.action_type === 'RECEIVE' 
                      ? `Stock will be DEDUCTED by ${logToDelete.quantity} units` 
                      : logToDelete.action_type === 'PULL_OUT'
                      ? `Stock will be RESTORED by ${logToDelete.quantity} units`
                      : logToDelete.action_type === 'RECOUNT'
                      ? `Stock will be restored to previous value`
                      : logToDelete.action_type?.includes('TRANSFER')
                      ? `Transfer will be reversed at both branches`
                      : `Action will be reversed`
                  }
                </p>
              </div>
              
              <p className="text-sm text-gray-500">
                This action cannot be undone. A deletion log will be created for audit purposes.
              </p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setLogToDelete(null)}>Cancel</Button>
            <Button variant="destructive" onClick={executeDeleteLog}>
              <Trash2 className="h-4 w-4 mr-1" />
              Delete & Reverse
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Item Manager Modal */}
      <Dialog open={showItemManager} onOpenChange={setShowItemManager}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-purple-600" />
              Item Manager
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Search and Actions */}
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search items..."
                  value={itemSearchQuery}
                  onChange={(e) => setItemSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="item-manager-search"
                />
              </div>
              <Button
                variant="outline"
                onClick={() => setShowInactive(!showInactive)}
                className={showInactive ? 'bg-purple-50 border-purple-300' : ''}
              >
                {showInactive ? <Eye className="h-4 w-4 mr-1" /> : <EyeOff className="h-4 w-4 mr-1" />}
                {showInactive ? 'Show All' : 'Active Only'}
              </Button>
              <Button
                onClick={() => setShowAddItem(true)}
                className="bg-green-600 hover:bg-green-700"
                data-testid="add-new-item-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Item
              </Button>
            </div>

            {/* Item List */}
            <div className="max-h-[50vh] overflow-y-auto border rounded-lg">
              <table className="w-full">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Item Name</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {allItems
                    .filter(item => {
                      const matchesSearch = !itemSearchQuery || 
                        item.item_name?.toLowerCase().includes(itemSearchQuery.toLowerCase()) ||
                        item.category?.toLowerCase().includes(itemSearchQuery.toLowerCase());
                      const matchesStatus = showInactive || item.status === 'ACTIVE' || !item.status;
                      return matchesSearch && matchesStatus;
                    })
                    .slice(0, 100)
                    .map(item => (
                      <tr key={item.id} className={`hover:bg-gray-50 ${item.status === 'INACTIVE' ? 'opacity-50' : ''}`}>
                        <td className="px-4 py-2">
                          <div className="font-medium text-sm">{item.item_name}</div>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600">{item.category}</td>
                        <td className="px-4 py-2 text-center">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                            item.status === 'INACTIVE' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'
                          }`}>
                            {item.status === 'INACTIVE' ? 'HIDDEN' : 'VISIBLE'}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => toggleItemStatus(item)}
                            className={item.status === 'INACTIVE' 
                              ? 'text-green-600 border-green-200 hover:bg-green-50' 
                              : 'text-orange-600 border-orange-200 hover:bg-orange-50'
                            }
                            data-testid={`toggle-status-${item.id}`}
                          >
                            {item.status === 'INACTIVE' ? (
                              <><Eye className="h-3 w-3 mr-1" /> Unhide</>
                            ) : (
                              <><EyeOff className="h-3 w-3 mr-1" /> Hide</>
                            )}
                          </Button>
                        </td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>

            <div className="text-sm text-gray-500">
              Showing {allItems.filter(i => showInactive || i.status !== 'INACTIVE').length} items
              {!showInactive && allItems.filter(i => i.status === 'INACTIVE').length > 0 && 
                ` (${allItems.filter(i => i.status === 'INACTIVE').length} hidden)`
              }
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowItemManager(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pending Transfers Modal */}
      {/* OUTGOING PENDING TRANSFERS MODAL (sender-side status view) */}
      <Dialog open={showOutgoingPending} onOpenChange={setShowOutgoingPending}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="outgoing-pending-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <ArrowRightLeft className="h-5 w-5" />
              Outgoing Transfers — {selectedBranch}
            </DialogTitle>
            <DialogDescription>
              Mga ipinadala niyong stock na HINDI PA ina-accept ng receiving branch. Naka-deduct na ito sa inyong stock (IN TRANSIT). I-follow up ang receiving branch para i-accept o i-reject nila.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {outgoingPending.length === 0 ? (
              <p className="text-center text-gray-500 py-6">Wala nang naghihintay na outgoing transfers 🎉</p>
            ) : (
              outgoingPending.map((t, idx) => (
                <div key={t.id || idx} className="border border-blue-200 bg-blue-50/50 rounded-lg p-3" data-testid={`outgoing-pending-item-${idx}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold text-gray-800">{t.item_name}</p>
                      <p className="text-sm text-gray-600">
                        Qty: <span className="font-bold">{t.quantity}</span>
                        {t.source_location && <span className="text-gray-400"> mula {t.source_location}</span>}
                      </p>
                      <p className="text-sm text-gray-600">
                        Papunta: <span className="font-bold text-blue-700">{t.destination_branch}</span>
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        Ipinadala: {t.sent_at ? new Date(t.sent_at).toLocaleString() : "—"}
                        {t.sent_by && <span> • ni {t.sent_by}</span>}
                      </p>
                    </div>
                    <span className="text-xs font-semibold bg-amber-100 text-amber-700 px-2 py-1 rounded-full whitespace-nowrap">IN TRANSIT</span>
                  </div>
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOutgoingPending(false)} data-testid="close-outgoing-pending">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showPendingTransfers} onOpenChange={setShowPendingTransfers}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <Inbox className="h-5 w-5" />
              Incoming Transfers - {selectedBranch}
            </DialogTitle>
            <DialogDescription>
              I-confirm ang mga incoming transfers — ilagay kung ilan TALAGA ang natanggap at kung saan ilalagay.
            </DialogDescription>
          </DialogHeader>
          <ErrorBoundary onReset={() => fetchPendingTransfers()}>
          <div className="py-4">
            {(!pendingTransfers || pendingTransfers.length === 0) ? (
              <div className="text-center py-8 text-gray-500">
                <Inbox className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No pending transfers</p>
                <p className="text-sm">All incoming transfers have been processed</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[50vh] overflow-y-auto">
                {pendingTransfers
                  .filter(t => t && typeof t === 'object' && (t.id || t.row_id))
                  .map(transfer => (
                  <div 
                    key={transfer.row_id || transfer.id} 
                    className="p-4 border-2 border-orange-200 rounded-lg bg-orange-50"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-semibold text-gray-900">{transfer.item_name}</div>
                        <div className="text-sm text-gray-600">
                          Quantity: <span className="font-bold">{transfer.stocks_qty}</span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          From: {transfer.source_branch || transfer.transfer_from_branch} • {transfer.created_at || transfer.date_received}
                        </div>
                      </div>
                      <span className="px-2 py-1 bg-orange-200 text-orange-800 text-xs font-medium rounded">
                        PENDING LOCATION
                      </span>
                    </div>
                    
                    {/* Accept/Reject with location + ANTI-THEFT received qty */}
                    <div className="mt-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          <Select 
                            value={(acceptingTransfer?.id || acceptingTransfer?.row_id) === (transfer.id || transfer.row_id) ? acceptLocation : ""} 
                            onValueChange={(val) => {
                              setAcceptingTransfer(transfer);
                              setAcceptLocation(val);
                              setAcceptReceivedQty(String(transfer.stocks_qty || transfer.quantity || 0));
                              setAcceptVarianceReason("");
                            }}
                          >
                            <SelectTrigger className="bg-white">
                              <MapPin className="h-4 w-4 mr-2 text-orange-500" />
                              <SelectValue placeholder="Where will you place this?" />
                            </SelectTrigger>
                            <SelectContent>
                              {branchLocations.map(loc => (
                                <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <Button
                          onClick={() => {
                            const transferId = transfer.id || transfer.row_id;
                            const acceptingId = acceptingTransfer?.id || acceptingTransfer?.row_id;
                            if (acceptingId === transferId && acceptLocation) {
                              acceptPendingTransfer();
                            } else {
                              setAcceptingTransfer(transfer);
                              setAcceptLocation("Display Area");
                              setAcceptReceivedQty(String(transfer.stocks_qty || transfer.quantity || 0));
                              setAcceptVarianceReason("");
                            }
                          }}
                          disabled={processing || ((acceptingTransfer?.id || acceptingTransfer?.row_id) === (transfer.id || transfer.row_id) && !acceptLocation)}
                          className="bg-green-600 hover:bg-green-700"
                          data-testid={`accept-transfer-${transfer.id || transfer.row_id}`}
                        >
                          <Check className="h-4 w-4 mr-1" />
                          {processing && (acceptingTransfer?.id || acceptingTransfer?.row_id) === (transfer.id || transfer.row_id) ? "..." : "Accept"}
                        </Button>
                        <Button
                          onClick={() => setRejectingTransfer(transfer)}
                          disabled={processing}
                          variant="outline"
                          className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                          data-testid={`reject-transfer-${transfer.id || transfer.row_id}`}
                        >
                          <X className="h-4 w-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                      
                      {/* ANTI-THEFT: Ilan TALAGA ang natanggap? */}
                      {(acceptingTransfer?.id || acceptingTransfer?.row_id) === (transfer.id || transfer.row_id) && (
                        <div className="p-3 bg-white border border-orange-200 rounded-lg space-y-2">
                          <div className="flex items-center gap-2">
                            <label className="text-xs font-semibold text-gray-700 whitespace-nowrap">📦 Ilan ang NATANGGAP mo?</label>
                            <Input
                              type="number"
                              min="0"
                              max={transfer.stocks_qty || transfer.quantity || 0}
                              value={acceptReceivedQty}
                              onChange={(e) => setAcceptReceivedQty(e.target.value)}
                              className="w-24 h-8 text-center font-bold"
                              data-testid={`received-qty-${transfer.id || transfer.row_id}`}
                            />
                            <span className="text-xs text-gray-500">/ {transfer.stocks_qty || transfer.quantity} pinadala</span>
                          </div>
                          {Number(acceptReceivedQty) < (transfer.stocks_qty || transfer.quantity || 0) && (
                            <div className="space-y-1">
                              <p className="text-xs font-semibold text-red-600">⚠️ KULANG ng {(transfer.stocks_qty || transfer.quantity || 0) - Number(acceptReceivedQty)}! Ma-fa-flag ito bilang variance at makikita ng admin.</p>
                              <Input
                                placeholder="Bakit kulang? (required, min 5 chars) — hal. 'sira ang 2 pcs', 'kulang sa kahon'"
                                value={acceptVarianceReason}
                                onChange={(e) => setAcceptVarianceReason(e.target.value)}
                                className="h-8 text-xs border-red-300"
                                data-testid={`variance-reason-${transfer.id || transfer.row_id}`}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          </ErrorBoundary>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPendingTransfers(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Transfer Confirmation Modal */}
      <Dialog open={!!rejectingTransfer} onOpenChange={(open) => !open && setRejectingTransfer(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Undo2 className="h-5 w-5" />
              Reject Transfer & Return Stock
            </DialogTitle>
          </DialogHeader>
          <ErrorBoundary onReset={() => { setRejectingTransfer(null); setRejectReason(""); }}>
          {rejectingTransfer && (
            <div className="py-4 space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="font-medium text-red-800 mb-2">You are rejecting:</p>
                <div className="space-y-1 text-sm">
                  <div><span className="text-gray-600">Item:</span> <span className="font-medium">{rejectingTransfer.item_name}</span></div>
                  <div><span className="text-gray-600">Quantity:</span> <span className="font-medium">{rejectingTransfer.stocks_qty}</span></div>
                  <div><span className="text-gray-600">From Branch:</span> <span className="font-medium">{rejectingTransfer.transfer_from_branch}</span></div>
                </div>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-yellow-800 text-sm">
                  <strong>Note:</strong> Stock will be RETURNED to <strong>{rejectingTransfer.transfer_from_branch}</strong>. The {rejectingTransfer.stocks_qty} units will be added back to their inventory.
                </p>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Reason for rejection <span className="text-red-500">* (required, min 5 chars)</span></label>
                <Input
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="e.g., Wrong item, damaged, not needed..."
                  data-testid="reject-reason-input"
                />
              </div>
            </div>
          )}
          </ErrorBoundary>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejectingTransfer(null); setRejectReason(""); }}>
              Cancel
            </Button>
            <Button 
              onClick={rejectPendingTransfer} 
              disabled={processing || rejectReason.trim().length < 5}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-reject-transfer"
            >
              {processing ? "Rejecting..." : "Reject & Return Stock"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Incoming Item Requests Modal */}
      <Dialog open={showIncomingRequests} onOpenChange={setShowIncomingRequests}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600">
              <ArrowRightLeft className="h-5 w-5" />
              Item Requests from Other Branches - {selectedBranch}
            </DialogTitle>
          </DialogHeader>
          <ErrorBoundary onReset={() => fetchIncomingRequests()}>
          <div className="py-4">
            {(!incomingRequests || incomingRequests.length === 0) ? (
              <div className="text-center py-8 text-gray-500">
                <ArrowRightLeft className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No pending requests</p>
                <p className="text-sm">No other branches have requested items from {selectedBranch}</p>
              </div>
            ) : (() => {
              // Group requests by source branch, then by date
              const validRequests = incomingRequests.filter(r => r && typeof r === 'object' && (r.request_id || r._id));
              const grouped = {};
              validRequests.forEach(r => {
                const fromBranch = r.destination_branch || 'Unknown';
                let dateKey = 'Unknown';
                try {
                  if (r.requested_at) {
                    dateKey = new Date(r.requested_at).toISOString().split('T')[0];
                  }
                } catch (e) { /* ignore */ }
                if (!grouped[fromBranch]) grouped[fromBranch] = {};
                if (!grouped[fromBranch][dateKey]) grouped[fromBranch][dateKey] = [];
                grouped[fromBranch][dateKey].push(r);
              });
              
              // Sort branches alphabetically and dates desc
              const sortedBranches = Object.keys(grouped).sort();
              
              return (
                <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                  {/* Top summary */}
                  <div className="px-3 py-2 bg-blue-100 border border-blue-300 rounded-md text-sm text-blue-900">
                    📊 Total pending: <strong>{validRequests.length}</strong> requests galing sa <strong>{sortedBranches.length}</strong> branch(es)
                  </div>
                  
                  {sortedBranches.map(fromBranch => {
                    const branchTotal = Object.values(grouped[fromBranch]).reduce((sum, arr) => sum + arr.length, 0);
                    const sortedDates = Object.keys(grouped[fromBranch]).sort((a, b) => b.localeCompare(a));
                    return (
                      <div key={fromBranch} className="border-2 border-blue-300 rounded-lg overflow-hidden">
                        {/* Branch Header */}
                        <div className="bg-gradient-to-r from-blue-100 to-blue-50 px-3 py-2 border-b-2 border-blue-300">
                          <div className="flex items-center justify-between">
                            <div className="font-bold text-blue-900 flex items-center gap-2">
                              <ArrowRightLeft className="h-4 w-4" />
                              From {fromBranch}
                            </div>
                            <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full font-semibold">
                              {branchTotal} request{branchTotal > 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                        
                        {sortedDates.map(date => (
                          <div key={`${fromBranch}-${date}`} className="border-b last:border-b-0">
                            {/* Date Sub-header */}
                            <div className="bg-blue-50/50 px-3 py-1.5 border-b border-blue-200 flex items-center justify-between">
                              <span className="text-xs font-semibold text-blue-800">📅 {date}</span>
                              <span className="text-xs text-blue-600">{grouped[fromBranch][date].length} item(s)</span>
                            </div>
                            
                            {/* Requests for this date */}
                            <div className="space-y-2 p-2 bg-white">
                              {grouped[fromBranch][date].map(request => {
                                let formattedTime = '';
                                try {
                                  if (request.requested_at) {
                                    formattedTime = new Date(request.requested_at).toLocaleTimeString('en-PH', { hour: '2-digit', minute: '2-digit' });
                                  }
                                } catch (e) { /* ignore */ }
                                
                                return (
                  <div 
                    key={request.request_id || request._id || Math.random()} 
                    className="p-4 border-2 border-blue-200 rounded-lg bg-blue-50"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-semibold text-gray-900">{request.item_name || 'Unknown Item'}</div>
                        <div className="text-sm text-gray-600">
                          Quantity requested: {(() => {
                            const q = request.quantity;
                            if (q === null || q === undefined) {
                              return <span className="font-bold text-red-600">⚠️ Missing</span>;
                            }
                            const numQ = Number(q);
                            const hasFraction = numQ % 1 !== 0;
                            return (
                              <span className={`font-bold ${hasFraction ? 'text-orange-600' : 'text-gray-900'}`}>
                                {numQ}
                                {hasFraction && (
                                  <span className="ml-1 text-xs px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded">
                                    PARTIAL ({numQ % 1 === 0.5 ? '½' : 'fraction'})
                                  </span>
                                )}
                              </span>
                            );
                          })()}
                        </div>
                        <div className="text-sm text-gray-600">
                          Available: <span className="font-bold text-green-600">{request.available_stock_at_request || 'Unknown'}</span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          Requested at: {formattedTime || 'time unknown'}
                        </div>
                        {request.notes && (
                          <div className="text-xs text-gray-500 mt-1">
                            Notes: {request.notes}
                          </div>
                        )}
                      </div>
                      <span className="px-2 py-1 bg-blue-200 text-blue-800 text-xs font-medium rounded">
                        PENDING APPROVAL
                      </span>
                    </div>
                    
                    {/* Approve/Reject with location selection */}
                    <div className="mt-4 flex items-center gap-2">
                      <div className="flex-1">
                        <Select 
                          value={approvingRequest?.request_id === request.request_id ? approveLocation : ""} 
                          onValueChange={(val) => {
                            setApprovingRequest(request);
                            setApproveLocation(val);
                          }}
                        >
                          <SelectTrigger className="bg-white">
                            <MapPin className="h-4 w-4 mr-2 text-blue-500" />
                            <SelectValue placeholder="Select location to deduct from" />
                          </SelectTrigger>
                          <SelectContent>
                            {branchLocations.map(loc => (
                              <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        onClick={() => {
                          if (approvingRequest?.request_id === request.request_id && approveLocation) {
                            handleApproveRequest();
                          } else {
                            setApprovingRequest(request);
                            setApproveLocation("Display Area");
                          }
                        }}
                        disabled={processing || (approvingRequest?.request_id === request.request_id && !approveLocation)}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid={`approve-request-${request.request_id}`}
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Approve
                      </Button>
                      <Button
                        onClick={() => setRejectingRequest(request)}
                        disabled={processing}
                        variant="outline"
                        className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                        data-testid={`reject-request-${request.request_id}`}
                      >
                        <X className="h-4 w-4 mr-1" />
                        Reject
                      </Button>
                    </div>
                  </div>
                  );
                })}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
          </ErrorBoundary>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowIncomingRequests(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Request Confirmation Modal */}
      <Dialog open={!!rejectingRequest} onOpenChange={(open) => !open && setRejectingRequest(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <X className="h-5 w-5" />
              Reject Item Request
            </DialogTitle>
          </DialogHeader>
          <ErrorBoundary onReset={() => setRejectingRequest(null)}>
          {rejectingRequest && (
            <div className="py-4 space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="font-medium text-red-800 mb-2">You are rejecting a request from {rejectingRequest.destination_branch || 'Unknown'}:</p>
                <div className="space-y-1 text-sm">
                  <div><span className="text-gray-600">Item:</span> <span className="font-medium">{rejectingRequest.item_name || 'Unknown'}</span></div>
                  <div><span className="text-gray-600">Quantity:</span> <span className="font-medium">{rejectingRequest.quantity || 0}</span></div>
                  <div><span className="text-gray-600">Requested By:</span> <span className="font-medium">{rejectingRequest.requested_by || 'Unknown'}</span></div>
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Reason for rejection (optional)</label>
                <Input
                  value={requestRejectReason}
                  onChange={(e) => setRequestRejectReason(e.target.value)}
                  placeholder="e.g., Out of stock, need items for own branch..."
                  data-testid="reject-request-reason-input"
                />
              </div>
            </div>
          )}
          </ErrorBoundary>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejectingRequest(null); setRequestRejectReason(""); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleRejectRequest} 
              disabled={processing}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-reject-request"
            >
              Reject Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* My Outgoing Requests Modal - requests I made to other branches */}
      <Dialog open={showOutgoingRequests} onOpenChange={setShowOutgoingRequests}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <ArrowRightLeft className="h-5 w-5" />
              My Outgoing Requests - {selectedBranch}
            </DialogTitle>
            <p className="text-sm text-gray-500">Requests you made to other branches</p>
          </DialogHeader>
          <div className="py-4 max-h-96 overflow-y-auto">
            {outgoingRequests.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <ArrowRightLeft className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p className="font-medium">No pending requests</p>
                <p className="text-sm">You haven't requested items from other branches</p>
              </div>
            ) : (
              <div className="space-y-3">
                {outgoingRequests.map(request => (
                  <div 
                    key={request.request_id || request._id} 
                    className="p-4 border-2 border-green-200 rounded-lg bg-green-50"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-semibold text-gray-900">{request.item_name || 'Unknown Item'}</div>
                        <div className="text-sm text-gray-600">
                          Quantity requested: <span className="font-bold">{request.quantity || 0}</span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          Requested from: <span className="font-medium text-green-700">{request.source_branch || 'Unknown'}</span>
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          {request.requested_at ? new Date(request.requested_at).toLocaleString() : ''}
                        </div>
                        {request.notes && (
                          <div className="text-xs text-gray-500 mt-1 italic">
                            Notes: {request.notes}
                          </div>
                        )}
                      </div>
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                        Pending
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOutgoingRequests(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pending Recounts Modal - Admin Only */}
      <Dialog open={showPendingRecounts} onOpenChange={setShowPendingRecounts}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <RefreshCw className="h-5 w-5" />
              Pending Recount Approvals
            </DialogTitle>
            <p className="text-sm text-gray-500">Review and approve/reject recount submissions from staff</p>
          </DialogHeader>
          <div className="py-4 max-h-96 overflow-y-auto">
            {pendingRecounts.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <RefreshCw className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p className="font-medium">No pending recounts</p>
                <p className="text-sm">All recount requests have been processed</p>
              </div>
            ) : (
              <div className="space-y-3">
                {pendingRecounts.map(recount => (
                  <div 
                    key={recount.id} 
                    className="p-4 border-2 border-amber-200 rounded-lg bg-amber-50"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900">{recount.item_name}</div>
                        <div className="text-sm text-gray-600">
                          Branch: <span className="font-medium">{recount.branch}</span> • 
                          Location: <span className="font-medium">{recount.location}</span>
                        </div>
                        <div className="text-sm mt-1">
                          <span className="text-red-600 font-medium">{recount.old_qty}</span>
                          <span className="mx-2">→</span>
                          <span className="text-green-600 font-medium">{recount.new_qty}</span>
                          <span className={`ml-2 text-xs px-1 rounded ${recount.difference >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {recount.difference >= 0 ? '+' : ''}{recount.difference}
                          </span>
                        </div>
                        {recount.notes && (
                          <div className="text-xs text-gray-500 mt-1 italic">
                            Notes: {recount.notes}
                          </div>
                        )}
                        <div className="text-xs text-gray-400 mt-1">
                          Submitted by: {recount.submitted_by} • {recount.submitted_at ? new Date(recount.submitted_at).toLocaleString() : ''}
                        </div>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <Button
                          size="sm"
                          onClick={async () => {
                            try {
                              const token = localStorage.getItem("auth_token");
                              await axios.put(`${API}/recount/${recount.id}/approve`, null, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              toast.success(`✅ Recount approved: ${recount.item_name}`);
                              fetchPendingRecounts();
                              fetchInventory();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || "Failed to approve");
                            }
                          }}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          <Check className="h-4 w-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            const reason = prompt("Reason for rejection (optional):");
                            try {
                              const token = localStorage.getItem("auth_token");
                              await axios.put(`${API}/recount/${recount.id}/reject`, null, {
                                headers: { Authorization: `Bearer ${token}` },
                                params: { reason: reason || "" }
                              });
                              toast.success(`Recount rejected: ${recount.item_name}`);
                              fetchPendingRecounts();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || "Failed to reject");
                            }
                          }}
                          className="border-red-300 text-red-600 hover:bg-red-50"
                        >
                          <X className="h-4 w-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPendingRecounts(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Custom Location Modal (for VZ) */}
      <Dialog open={showAddLocation} onOpenChange={setShowAddLocation}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-indigo-600" />
              Add New Location - {selectedBranch}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Location Name</label>
              <Input
                value={newLocationName}
                onChange={(e) => setNewLocationName(e.target.value)}
                placeholder="e.g., Back Storage, Shelf 3..."
                data-testid="new-location-input"
              />
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                Current locations: {branchLocations.join(", ")}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddLocation(false)}>Cancel</Button>
            <Button onClick={addCustomLocation} disabled={!newLocationName.trim()}>
              Add Location
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Location Modal */}
      <Dialog open={!!editingLocationItem} onOpenChange={(open) => { if (!open) { setEditingLocationItem(null); setEditingCurrentLocation(""); setEditCustomLocation(""); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-indigo-600" />
              Move Stock to Different Location
            </DialogTitle>
          </DialogHeader>
          {editingLocationItem && (
            <div className="py-4 space-y-4">
              {/* Item Info */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="font-semibold text-gray-900">{editingLocationItem.item_name}</div>
                <div className="text-sm text-gray-500">
                  Stock at this location: <span className="font-bold text-indigo-600">
                    {editingLocationItem.locations?.[editingCurrentLocation] || 0}
                  </span>
                </div>
              </div>
              
              {/* Current vs New Location */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-500">Current Location:</span>
                  <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded font-medium">
                    {editingCurrentLocation}
                  </span>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Move To:</label>
                  <Select value={newLocation} onValueChange={(val) => { setNewLocation(val); if (val !== "__add_new__") setEditCustomLocation(""); }}>
                    <SelectTrigger data-testid="edit-location-select">
                      <SelectValue placeholder="Select new location" />
                    </SelectTrigger>
                    <SelectContent>
                      {branchLocations.filter(loc => loc !== editingCurrentLocation).map(loc => (
                        <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                      ))}
                      <SelectItem value="__add_new__" className="text-green-600 font-medium">
                        <span className="flex items-center gap-1">
                          <Plus className="h-3 w-3" /> Add New Location...
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Custom Location Input - shows when "Add New Location" is selected */}
                {newLocation === "__add_new__" && (
                  <div className="space-y-2 p-3 bg-green-50 rounded-lg border border-green-200">
                    <label className="text-sm font-medium text-green-700">New Location Name:</label>
                    <Input
                      value={editCustomLocation}
                      onChange={(e) => setEditCustomLocation(e.target.value)}
                      placeholder="e.g., Storage Room B, Hallway 3..."
                      className="border-green-300 focus:border-green-500"
                      data-testid="edit-custom-location-input"
                    />
                    <p className="text-xs text-green-600">
                      💡 Ito ay mase-save sa branch locations para magamit ulit sa susunod.
                    </p>
                  </div>
                )}
                
                {/* Preview of the move */}
                {((newLocation && newLocation !== editingCurrentLocation && newLocation !== "__add_new__") || 
                  (newLocation === "__add_new__" && editCustomLocation.trim())) && (
                  <div className="flex items-center gap-2 text-indigo-600 font-medium p-2 bg-indigo-50 rounded">
                    <ArrowRightLeft className="h-4 w-4" />
                    <span>{editingCurrentLocation}</span>
                    <span>→</span>
                    <span>{newLocation === "__add_new__" ? editCustomLocation.trim() : newLocation}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditingLocationItem(null); setEditingCurrentLocation(""); setEditCustomLocation(""); }}>Cancel</Button>
            <Button 
              onClick={saveItemLocation} 
              disabled={savingLocation || !newLocation || newLocation === editingCurrentLocation || (newLocation === "__add_new__" && !editCustomLocation.trim())}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {savingLocation ? "Moving..." : "Move Stock"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Box Size Modal */}
      <Dialog open={!!editingBoxItem} onOpenChange={(open) => { if (!open) { setEditingBoxItem(null); setNewSetsPerBox(1); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-amber-600" />
              Set Units Per Box
            </DialogTitle>
          </DialogHeader>
          {editingBoxItem && (
            <div className="py-4 space-y-4">
              {/* Item Info */}
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <div className="font-semibold text-gray-900">{editingBoxItem.item_name}</div>
                <div className="text-sm text-gray-500">
                  Current stock: <span className="font-bold text-amber-600">{editingBoxItem.total_qty} units</span>
                </div>
              </div>
              
              {/* Box Size Input */}
              <div className="space-y-3">
                <label className="text-sm font-medium text-gray-700">
                  How many units in 1 box of this item?
                </label>
                <div className="flex items-center gap-3">
                  <Input
                    type="number"
                    min="1"
                    value={newSetsPerBox}
                    onChange={(e) => setNewSetsPerBox(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-32 text-center text-lg font-bold"
                    data-testid="box-size-input"
                  />
                  <span className="text-gray-600">units per box</span>
                </div>
                
                {/* Preview calculation */}
                {newSetsPerBox > 1 && (
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="text-sm text-green-700">
                      <span className="font-medium">Preview:</span> {editingBoxItem.total_qty} units = {' '}
                      <span className="font-bold text-green-800">
                        {Math.floor(editingBoxItem.total_qty / newSetsPerBox)} boxes
                        {editingBoxItem.total_qty % newSetsPerBox > 0 && (
                          <span className="text-amber-600"> + {editingBoxItem.total_qty % newSetsPerBox} loose</span>
                        )}
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Common box sizes */}
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs text-gray-500">Common sizes:</span>
                  {[10, 12, 20, 24, 40, 48, 50, 100].map(size => (
                    <button
                      key={size}
                      onClick={() => setNewSetsPerBox(size)}
                      className={`px-2 py-1 text-xs rounded ${newSetsPerBox === size ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditingBoxItem(null); setNewSetsPerBox(1); }}>Cancel</Button>
            <Button 
              onClick={saveBoxSize} 
              disabled={savingBoxSize || newSetsPerBox < 1}
              className="bg-amber-500 hover:bg-amber-600"
            >
              {savingBoxSize ? "Saving..." : "Save Box Size"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add New Item Modal */}
      <Dialog open={showAddItem} onOpenChange={setShowAddItem}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-green-600" />
              Add New Item to Master List
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                💡 Ito ay magdaragdag ng bagong item sa Item Master list. Pagkatapos nito, lalabas na ang item sa lahat ng search/autocomplete fields.
              </p>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Item Name *</label>
              <Input
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                placeholder="e.g., Alpha White H4"
                data-testid="new-item-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Category (optional)</label>
              <Input
                value={newItemCategory}
                onChange={(e) => setNewItemCategory(e.target.value)}
                placeholder="e.g., Alpha, Air, Ultra..."
                data-testid="new-item-category-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddItem(false)}>Cancel</Button>
            <Button 
              onClick={addNewItem} 
              disabled={addingItem || !newItemName.trim()}
              className="bg-green-600 hover:bg-green-700"
            >
              {addingItem ? "Adding..." : "Add Item"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Item Confirmation Modal */}
      <Dialog open={!!itemToDelete} onOpenChange={(open) => !open && setItemToDelete(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Delete Inventory Record
            </DialogTitle>
          </DialogHeader>
          {itemToDelete && (
            <div className="py-4 space-y-4">
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <p className="text-sm text-red-800 font-medium mb-2">
                  ⚠️ Sigurado ka bang gusto mong i-delete ang record na ito?
                </p>
              </div>
              
              {/* Undo notice */}
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800">
                  🔄 <strong>May Undo!</strong> Pwede mong i-restore ang deleted record within <strong>15 minutes</strong>.
                </p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-500">Item:</span>
                  <span className="font-bold text-gray-900">{itemToDelete.item_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Branch:</span>
                  <span className="font-medium">{selectedBranch} - {BRANCHES[selectedBranch]}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Location:</span>
                  <span className="font-medium">{itemToDelete.location || "No location"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Current Stock:</span>
                  <span className="font-bold text-red-600">{itemToDelete.total_qty} units</span>
                </div>
              </div>
              
              {itemToDelete.total_qty > 0 && (
                <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <p className="text-sm text-yellow-800">
                    ⚠️ <strong>Warning:</strong> This item has {itemToDelete.total_qty} units in stock. 
                    Stock count will be removed but can be restored within 15 minutes.
                  </p>
                </div>
              )}

              {/* ANTI-THEFT: Admin check + Mandatory deletion reason */}
              {!isAdmin() ? (
                <div className="p-3 bg-red-100 rounded-lg border border-red-300">
                  <p className="text-sm text-red-800 font-medium flex items-center gap-2">
                    <Lock className="h-4 w-4" />
                    Admin lang ang pwedeng mag-delete ng records. Contact your administrator.
                  </p>
                </div>
              ) : (
                <div className="space-y-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <label className="text-sm font-medium text-amber-900 flex items-center gap-1">
                    <Lock className="h-4 w-4 text-amber-700" />
                    Reason for Deletion * (min 10 chars)
                  </label>
                  <Input
                    value={deleteReason}
                    onChange={(e) => setDeleteReason(e.target.value)}
                    placeholder="e.g., Duplicate record, wrong entry on 2026-06-01"
                    className="bg-white"
                    data-testid="delete-reason-input"
                  />
                  <p className="text-xs text-amber-700">
                    Naka-log sa Stock Audit. Hindi pwedeng burahin nang walang explanation.
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setItemToDelete(null); setDeleteReason(""); }} disabled={deletingItem}>
              Cancel
            </Button>
            <Button 
              onClick={deleteInventoryItem} 
              disabled={deletingItem || !isAdmin() || !deleteReason || deleteReason.trim().length < 10}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              {deletingItem ? "Deleting..." : "Delete Record"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Full Item History Modal */}
      <Dialog open={showFullHistory} onOpenChange={setShowFullHistory}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-blue-600" />
              Complete History - {fullHistoryItem?.item_name}
            </DialogTitle>
            <DialogDescription>
              Lahat ng nangyari sa item na ito sa {selectedBranch} branch
            </DialogDescription>
          </DialogHeader>
          
          {/* Lookback selector */}
          <div className="flex items-center justify-between gap-2 pb-2 border-b">
            <div className="text-xs text-gray-600">
              📊 Showing last <strong>{historyLookbackDays}</strong> days
            </div>
            <div className="flex items-center gap-2">
              <Select 
                value={String(historyLookbackDays)}
                onValueChange={(v) => {
                  const newDays = parseInt(v);
                  setHistoryLookbackDays(newDays);
                  // Refetch with new range
                  if (fullHistoryItem?.item_name) {
                    setTimeout(() => fetchItemHistory(fullHistoryItem.item_name, true), 100);
                  }
                }}
              >
                <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="history-lookback-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="60">Last 60 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                  <SelectItem value="180">Last 180 days</SelectItem>
                  <SelectItem value="365">Last 1 year</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto pr-2">
            {loadingHistory ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
                <span className="ml-2 text-gray-500">Loading history...</span>
              </div>
            ) : itemHistory[fullHistoryItem?.item_name]?.length > 0 ? (
              <div className="space-y-2">
                {/* Summary Stats */}
                <div className="grid grid-cols-4 gap-2 mb-2 p-3 bg-gray-50 rounded-lg">
                  {(() => {
                    const logs = itemHistory[fullHistoryItem?.item_name] || [];
                    const receives = logs.filter(l => l.action_type === 'RECEIVE').reduce((sum, l) => sum + Math.abs(l.quantity || 0), 0);
                    const sales = logs.filter(l => l.action_type?.includes('SALE')).reduce((sum, l) => sum + Math.abs(l.quantity || 0), 0);
                    const pullouts = logs.filter(l => l.action_type === 'PULL_OUT').reduce((sum, l) => sum + Math.abs(l.quantity || 0), 0);
                    const returns = logs.filter(l => l.action_type?.includes('RETURN') || l.action_type?.includes('RTS')).reduce((sum, l) => sum + Math.abs(l.quantity || 0), 0);
                    
                    return (
                      <>
                        <div className="text-center">
                          <div className="text-lg font-bold text-green-600">+{receives}</div>
                          <div className="text-xs text-gray-500">Received</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-orange-600">-{sales}</div>
                          <div className="text-xs text-gray-500">Sales</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-red-600">-{pullouts}</div>
                          <div className="text-xs text-gray-500">Pull Out</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-blue-600">+{returns}</div>
                          <div className="text-xs text-gray-500">Returns</div>
                        </div>
                      </>
                    );
                  })()}
                </div>

                {/* Per-Location Breakdown — para makita kung saan nababawas */}
                {(() => {
                  const logs = itemHistory[fullHistoryItem?.item_name] || [];
                  const byLocation = {};
                  logs.forEach(l => {
                    const loc = (l.location || 'Unknown').trim();
                    if (!byLocation[loc]) byLocation[loc] = { added: 0, removed: 0, count: 0 };
                    const isDeduction = l.action_type?.includes('SALE') ||
                                        l.action_type === 'PULL_OUT' ||
                                        l.action_type?.includes('TRANSFER_OUT') ||
                                        l.action_type === 'WARRANTY_CLAIM' ||
                                        l.action_type === 'WARRANTY_REPLACEMENT' ||
                                        l.action_type === 'WARRANTY_DEFECTIVE' ||
                                        l.action_type === 'WARRANTY_RESHIP';
                    const qty = Math.abs(l.quantity || 0);
                    if (isDeduction) byLocation[loc].removed += qty;
                    else byLocation[loc].added += qty;
                    byLocation[loc].count += 1;
                  });
                  const locs = Object.entries(byLocation).sort((a, b) => (b[1].removed + b[1].added) - (a[1].removed + a[1].added));
                  if (locs.length === 0) return null;
                  return (
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="history-per-location">
                      <div className="text-xs font-bold text-blue-800 mb-2 flex items-center gap-1">
                        <Package className="h-3 w-3" />
                        Per-Location Breakdown (para mahuli ang anomaly)
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                        {locs.map(([loc, v]) => (
                          <div key={loc} className="flex items-center justify-between text-xs bg-white px-2 py-1 rounded border">
                            <span className="font-medium text-gray-800 truncate" title={loc}>{loc}</span>
                            <div className="flex items-center gap-2 whitespace-nowrap">
                              {v.added > 0 && <span className="text-green-700 font-semibold">+{v.added}</span>}
                              {v.removed > 0 && <span className="text-red-700 font-semibold">-{v.removed}</span>}
                              <span className="text-gray-400">({v.count})</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* All Logs */}
                {itemHistory[fullHistoryItem?.item_name].map((log, idx) => (
                  <div key={log.id || log._id || `${log.timestamp || 't'}-${log.action_type || 'a'}-${idx}`} className="flex items-start gap-3 p-3 bg-white border rounded-lg hover:bg-gray-50 transition-colors">
                    <div className={`px-2 py-1 rounded text-xs font-semibold whitespace-nowrap ${
                      log.action_type === 'RECEIVE' ? 'bg-green-100 text-green-700' :
                      log.action_type === 'PULL_OUT' ? 'bg-red-100 text-red-700' :
                      log.action_type === 'RECOUNT' ? 'bg-blue-100 text-blue-700' :
                      log.action_type === 'INTERNAL_MOVE' ? 'bg-indigo-100 text-indigo-700' :
                      log.action_type?.includes('TRANSFER') ? 'bg-purple-100 text-purple-700' :
                      log.action_type?.includes('SALE') ? 'bg-orange-100 text-orange-700' :
                      log.action_type?.includes('RETURN') || log.action_type?.includes('RTS') ? 'bg-teal-100 text-teal-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {log.action_type?.replace(/_/g, ' ') || 'ACTION'}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        {(() => {
                          if (log.action_type === 'RECOUNT') {
                            const delta = (log.quantity ?? log.difference ?? 0);
                            return delta < 0
                              ? <span className="text-lg font-bold text-red-600">-{Math.abs(delta)}</span>
                              : <span className="text-lg font-bold text-green-600">+{Math.abs(delta)}</span>;
                          }
                          if (log.action_type === 'INTERNAL_MOVE') {
                            return <span className="text-lg font-bold text-indigo-600">{Math.abs(log.quantity || 0)}</span>;
                          }
                          const isSaleOrDeduction = log.action_type?.includes('SALE') || 
                                                    log.action_type === 'PULL_OUT' || 
                                                    log.action_type?.includes('TRANSFER_OUT') ||
                                                    log.action_type === 'WARRANTY_CLAIM' ||
                                                    log.action_type === 'WARRANTY_REPLACEMENT' ||
                                                    log.action_type === 'WARRANTY_DEFECTIVE' ||
                                                    log.action_type === 'WARRANTY_RESHIP';
                          const absQty = Math.abs(log.quantity || 0);
                          
                          if (isSaleOrDeduction) {
                            return <span className="text-lg font-bold text-red-600">-{absQty}</span>;
                          } else {
                            return <span className="text-lg font-bold text-green-600">+{absQty}</span>;
                          }
                        })()}
                        <span className="text-gray-500">units</span>
                        {log.location && (
                          <span className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600 font-medium">
                            @ {log.location}
                          </span>
                        )}
                      </div>
                      {/* Explicit before → after with diff, per location */}
                      {(log.previous_stock !== undefined && log.new_stock !== undefined && log.location) && (() => {
                        const prev = Number(log.previous_stock) || 0;
                        const next = Number(log.new_stock) || 0;
                        const diff = next - prev;
                        const diffStr = diff >= 0 ? `+${diff}` : `${diff}`;
                        const diffColor = diff >= 0 ? 'text-green-700' : 'text-red-700';
                        return (
                          <div className="mt-1 px-2 py-1 bg-yellow-50 border border-yellow-200 rounded text-sm font-mono inline-block">
                            <span className="font-semibold text-gray-700">{log.location}:</span>{' '}
                            <span className="text-gray-900">{prev}</span>
                            <span className="text-gray-500"> → </span>
                            <span className="text-gray-900 font-bold">{next}</span>
                            <span className={`ml-2 font-bold ${diffColor}`}>({diffStr})</span>
                          </div>
                        );
                      })()}
                      <div className="text-sm text-gray-600 mt-1">
                        {log.timestamp && new Date(log.timestamp).toLocaleDateString('en-PH', { 
                          weekday: 'short',
                          year: 'numeric',
                          month: 'short', 
                          day: 'numeric', 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </div>
                      {log.encoder && (
                        <div className="text-xs text-gray-400 mt-0.5">by {log.encoder}</div>
                      )}
                      {/* Fallback: if no location attached, still show generic before/after */}
                      {(log.previous_stock !== undefined && log.new_stock !== undefined && !log.location) && (
                        <div className="text-xs text-gray-500 mt-0.5 font-mono">
                          Stock: {log.previous_stock} → {log.new_stock}
                        </div>
                      )}
                      {log.reference_no && (
                        <div className="text-xs text-gray-400">Ref: {log.reference_no}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No history found for this item
              </div>
            )}
          </div>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowFullHistory(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </ErrorBoundary>
  );
}
